import { LOWER_MATH_CHAPTERS, LOWER_SCIENCE_CHAPTERS } from '../../config/tutorialCatalogue';
import { resolveText } from '../../utils/bilingualResolver';

const bi = (english, hindi) => ({ english, hindi });
const biList = (items) => items.map((item) => (typeof item === 'string' ? bi(item, item) : item));

const CLASS_META = [
  { key: 'class10', label: 'Class 10' },
  { key: 'class9', label: 'Class 9' },
  { key: 'class8', label: 'Class 8' },
  { key: 'class7', label: 'Class 7' },
  { key: 'class6', label: 'Class 6' },
];

const SUBJECT_META = [
  { key: 'science', label: 'Science', color: '#0284c7', accent: '#e0f2fe' },
  { key: 'mathematics', label: 'Mathematics', color: '#ea580c', accent: '#ffedd5' },
];

const MATH_HINTS = {
  'chapter-1-real-numbers': {
    summary: bi(
      'Builds the number system from counting numbers to rational and irrational numbers, with decimal expansion rules and Euclid\'s algorithm.',
      'यह अध्याय गिनती की संख्याओं से लेकर परिमेय और अपरिमेय संख्याओं तक की संख्या प्रणाली बनाता है, साथ ही दशमलव विस्तार और यूक्लिड की विधि भी समझाता है।'
    ),
    focus: biList([
      'Classify numbers as natural, whole, integer, rational and irrational.',
      'Use decimal expansion and termination criteria to test rationality.',
      'Apply HCF and LCM ideas using the fundamental theorem of arithmetic.',
    ]),
    example: {
      problem: bi('Find the HCF of 84 and 126 using prime factorisation.', '84 और 126 का अभाज्य गुणनखंड विधि से HCF ज्ञात कीजिए।'),
      solution: bi('84 = 2^2 x 3 x 7 and 126 = 2 x 3^2 x 7, so HCF = 2 x 3 x 7 = 42.', '84 = 2^2 x 3 x 7 और 126 = 2 x 3^2 x 7, इसलिए HCF = 2 x 3 x 7 = 42।'),
    },
    revision: bi('Rational numbers terminate or repeat; irrational numbers do not terminate and do not repeat.', 'परिमेय संख्याएँ समाप्त होती हैं या बार-बार दोहराती हैं; अपरिमेय संख्याएँ न समाप्त होती हैं और न दोहराती हैं।'),
  },
  'chapter-2-polynomials': {
    summary: bi('Introduces algebraic expressions, zeroes of polynomials, and the link between graph shape and coefficients.', 'यह अध्याय बीजीय व्यंजकों, बहुपदों के शून्यों और ग्राफ के आकार तथा गुणांकों के संबंध को समझाता है।'),
    focus: biList([
      'Identify degree, coefficients and zeroes of a polynomial.',
      'Relate number of zeroes to the number of x-axis intersections.',
      'Use the division algorithm and coefficient relationships in simple problems.',
    ]),
    example: {
      problem: bi('If p(x) = x^2 - 5x + 6, find its zeroes.', 'यदि p(x) = x^2 - 5x + 6 है, तो इसके शून्य ज्ञात कीजिए।'),
      solution: bi('x^2 - 5x + 6 = (x - 2)(x - 3), so the zeroes are 2 and 3.', 'x^2 - 5x + 6 = (x - 2)(x - 3), इसलिए शून्य 2 और 3 हैं।'),
    },
    revision: bi('Linear polynomials have one zero, quadratic polynomials have at most two zeroes.', 'रैखिक बहुपद का एक शून्य होता है, जबकि द्विघात बहुपद के अधिकतम दो शून्य हो सकते हैं।'),
  },
  'chapter-3-linear-equations': {
    summary: bi('Covers pair of linear equations and the geometric meaning of consistent, inconsistent and dependent systems.', 'यह अध्याय दो रैखिक समीकरणों तथा सुसंगत, असुसंगत और आश्रित समीकरण-तंत्र के ज्यामितीय अर्थ को कवर करता है।'),
    focus: biList([
      'Solve by substitution, elimination and cross-multiplication.',
      'Interpret the graph of two lines as intersecting, parallel or coincident.',
      'Classify systems using the ratios of coefficients.',
    ]),
    example: {
      problem: bi('Solve 2x + y = 7 and x - y = 1.', '2x + y = 7 और x - y = 1 को हल कीजिए।'),
      solution: bi('Add the equations: 3x = 8, so x = 8/3. Substitute to get y = 5/3.', 'समीकरणों को जोड़ने पर 3x = 8 मिलता है, इसलिए x = 8/3। मान रखने पर y = 5/3 मिलता है।'),
    },
    revision: bi('One solution means intersecting lines, no solution means parallel lines, infinitely many means same line.', 'एक समाधान का अर्थ प्रतिच्छेद करने वाली रेखाएँ, कोई समाधान नहीं का अर्थ समांतर रेखाएँ, और अनंत समाधान का अर्थ एक ही रेखा है।'),
  },
  'chapter-4-quadratic': {
    summary: bi('Builds quadratic equations, factorisation, the quadratic formula and the discriminant test.', 'यह अध्याय द्विघात समीकरण, गुणनखंडन, द्विघात सूत्र और विविक्तकर के आधार पर परीक्षण को समझाता है।'),
    focus: biList([
      'Write quadratic equations in standard form ax^2 + bx + c = 0.',
      'Use factorisation or the quadratic formula to find roots.',
      'Read the discriminant to decide the nature of roots.',
    ]),
    example: {
      problem: bi('Solve x^2 - 7x + 12 = 0.', 'x^2 - 7x + 12 = 0 को हल कीजिए।'),
      solution: bi('x^2 - 7x + 12 = (x - 3)(x - 4), so x = 3 or x = 4.', 'x^2 - 7x + 12 = (x - 3)(x - 4), इसलिए x = 3 या x = 4।'),
    },
    revision: bi('If D > 0 roots are real and distinct; if D = 0 roots are equal; if D < 0 there are no real roots.', 'यदि D > 0 हो तो मूल वास्तविक और भिन्न होते हैं; यदि D = 0 हो तो मूल समान होते हैं; यदि D < 0 हो तो कोई वास्तविक मूल नहीं होते।'),
  },
  'chapter-5-ap': {
    summary: bi('Introduces arithmetic progressions, nth term formulae and the sum of first n terms.', 'यह अध्याय समान्तर श्रेणी, nवें पद के सूत्र और पहले n पदों के योग को समझाता है।'),
    focus: biList([
      'Identify first term a and common difference d.',
      'Use a_n = a + (n - 1)d to get any term.',
      'Apply S_n = n/2 [2a + (n - 1)d] for sums.',
    ]),
    example: {
      problem: bi('Find the 10th term of 3, 7, 11, 15, ...', '3, 7, 11, 15, ... का 10वाँ पद ज्ञात कीजिए।'),
      solution: bi('Here a = 3 and d = 4, so a_10 = 3 + 9 x 4 = 39.', 'यहाँ a = 3 और d = 4 है, इसलिए a_10 = 3 + 9 x 4 = 39।'),
    },
    revision: bi('In an AP, the difference between consecutive terms remains constant.', 'समान्तर श्रेणी में क्रमागत पदों का अंतर स्थिर रहता है।'),
  },
  'chapter-6-triangles': {
    summary: bi('Focuses on similarity of triangles, proportional sides and area ratios.', 'त्रिभुजों में समरूपता, समानुपाती भुजाओं और क्षेत्रफल अनुपात पर केंद्रित है।'),
    focus: biList(['Use the criteria for similarity: AA, SAS and SSS.', 'Apply the Basic Proportionality Theorem in proofs.', 'Relate corresponding sides and areas of similar triangles.']),
    example: {
      problem: bi('If two similar triangles have side ratio 2:3, what is the area ratio?', 'यदि दो समरूप त्रिभुजों का भुजा अनुपात 2:3 है, तो क्षेत्रफल अनुपात क्या होगा?'),
      solution: bi('Area ratio = square of side ratio = 4:9.', 'क्षेत्रफल अनुपात = भुजा अनुपात का वर्ग = 4:9।'),
    },
    revision: bi('Similar figures have the same shape, not necessarily the same size.', 'समरूप आकृतियों का आकार समान होता है, माप नहीं।'),
  },
  'chapter-7-coord': {
    summary: bi('Introduces the Cartesian plane, plotting points and simple geometric calculations using coordinates.', 'कार्तेसीय तल, बिंदु आलेखन और निर्देशांकों से सरल ज्यामितीय गणना को समझाता है।'),
    focus: biList(['Read and write ordered pairs as (x, y).', 'Locate points in all four quadrants.', 'Use coordinate ideas in distance and midpoint style problems later on.']),
    example: {
      problem: bi('Plot the point (-3, 4).', 'बिंदु (-3, 4) को आलेखित कीजिए।'),
      solution: bi('Move 3 units left from the origin and 4 units up; the point lies in Quadrant II.', 'मूल बिंदु से 3 इकाई बायीं ओर और 4 इकाई ऊपर; बिंदु द्वितीय चतुर्थांश में है।'),
    },
    revision: bi('x-coordinate is the horizontal distance; y-coordinate is the vertical distance.', 'x-निर्देशांक क्षैतिज दूरी है; y-निर्देशांक ऊर्ध्वाधर दूरी है।'),
  },
  'chapter-8-trigo': {
    summary: bi('Introduces trigonometric ratios for a right triangle and the standard angle values.', 'समकोण त्रिभुज के लिए त्रिकोणमितीय अनुपात और मानक कोणों के मान को समझाता है।'),
    focus: biList(['Remember sin, cos, tan and their reciprocal ratios.', 'Learn complementary angle relations and key identities.', 'Use values for 0, 30, 45, 60 and 90 degrees confidently.']),
    example: {
      problem: bi('If sin A = 3/5 in a right triangle, find cos A.', 'यदि समकोण त्रिभुज में sin A = 3/5 है, तो cos A ज्ञात कीजिए।'),
      solution: bi('Using sin^2 A + cos^2 A = 1, cos A = 4/5.', 'sin^2 A + cos^2 A = 1 से cos A = 4/5 मिलता है।'),
    },
    revision: bi('Trigonometric ratios connect angle and side lengths in right triangles.', 'त्रिकोणमितीय अनुपात समकोण त्रिभुजों में कोण और भुजाओं को जोड़ते हैं।'),
  },
  'chapter-9-app-trigo': {
    summary: bi('Applies trigonometry to heights, distances and angle of elevation/depression problems.', 'ऊँचाई, दूरी और उन्नयन/अवनमन कोण की समस्याओं पर त्रिकोणमिति का अनुप्रयोग।'),
    focus: biList(['Translate real-life geometry into a right triangle.', 'Choose the correct ratio from the situation given.', 'Track units carefully in height-distance questions.']),
    example: {
      problem: bi('A tower subtends an angle of elevation 45 degrees from a point 20 m away. Find its height.', 'एक टावर 20 m दूर से 45° के उन्नयन कोण पर दिखता है। इसकी ऊँचाई ज्ञात कीजिए।'),
      solution: bi('tan 45 = h/20, so h = 20 m.', 'tan 45 = h/20, इसलिए h = 20 m।'),
    },
    revision: bi('Draw the diagram first; almost every question becomes simple after the picture is right.', 'पहले चित्र बनाएँ; चित्र सही होने के बाद लगभग हर प्रश्न सरल हो जाता है।'),
  },
  'chapter-10-circles': {
    summary: bi('Introduces tangents to circles and the standard tangent properties used in proofs.', 'वृत्तों पर स्पर्श रेखाएँ और प्रमाण में उपयोग की जाने वाली मानक स्पर्श-रेखा गुणधर्मों को समझाता है।'),
    focus: biList(['A tangent touches a circle at exactly one point.', 'A radius to the point of contact is perpendicular to the tangent.', 'Use equal tangent lengths from an external point.']),
    example: {
      problem: bi('If AP and AQ are tangents from A to a circle, compare AP and AQ.', 'यदि AP और AQ एक वृत्त पर A से खींची गई स्पर्श रेखाएँ हैं, तो AP और AQ की तुलना कीजिए।'),
      solution: bi('AP = AQ because tangents drawn from the same external point are equal.', 'AP = AQ क्योंकि एक ही बाह्य बिंदु से खींची स्पर्श रेखाएँ बराबर होती हैं।'),
    },
    revision: bi('The tangent radius property is the backbone of most circle proofs.', 'स्पर्शरेखा-त्रिज्या गुणधर्म अधिकांश वृत्त प्रमाणों का आधार है।'),
  },
  'chapter-11-areas': {
    summary: bi('Develops sectors, segments and area formulas involving circles.', 'वृत्त के सेक्टर, खंड और क्षेत्रफल सूत्रों को समझाता है।'),
    focus: biList(['Use area of sector and segment formulae carefully.', 'Track angle and radius units consistently.', 'Break complex shapes into simpler circular pieces.']),
    example: {
      problem: bi('Find the area of a sector of radius 7 cm and angle 90 degrees.', '7 cm त्रिज्या और 90° कोण वाले सेक्टर का क्षेत्रफल ज्ञात कीजिए।'),
      solution: bi('Area = 90/360 x pi x 7^2 = 49pi/4 cm^2.', 'क्षेत्रफल = 90/360 × π × 7^2 = 49π/4 cm^2।'),
    },
    revision: bi('Sector area is proportional to the central angle.', 'सेक्टर का क्षेत्रफल केंद्रीय कोण के समानुपाती होता है।'),
  },
  'chapter-12-volume': {
    summary: bi('Covers surface area and volume of cubes, cuboids, cylinders, cones and spheres.', 'घन, घनाभ, बेलन, शंकु और गोले के पृष्ठीय क्षेत्रफल और आयतन को समझाता है।'),
    focus: biList(['Remember formulae for curved and total surface area.', 'Use unit conversion before plugging into volume formulae.', 'Combine solids when the question is composite in nature.']),
    example: {
      problem: bi('Find the volume of a cuboid of dimensions 3 cm, 4 cm and 5 cm.', '3 cm, 4 cm और 5 cm माप वाले घनाभ का आयतन ज्ञात कीजिए।'),
      solution: bi('Volume = l x b x h = 3 x 4 x 5 = 60 cm^3.', 'आयतन = l × b × h = 3 × 4 × 5 = 60 cm^3।'),
    },
    revision: bi('Area is measured in square units, volume in cubic units.', 'क्षेत्रफल वर्ग इकाई में और आयतन घन इकाई में होता है।'),
  },
  'chapter-13-stats': {
    summary: bi('Introduces data handling, mean, median and mode in a school-friendly way.', 'स्कूल स्तर पर आँकड़ों का प्रबंधन, माध्य, माध्यिका और बहुलक को समझाता है।'),
    focus: biList(['Organise raw data into a frequency table.', 'Find average using the correct formula for the given data type.', 'Read bar graphs and histograms with confidence.']),
    example: {
      problem: bi('Find the mean of 2, 4, 6, 8.', '2, 4, 6, 8 का माध्य ज्ञात कीजिए।'),
      solution: bi('Mean = (2 + 4 + 6 + 8)/4 = 5.', 'माध्य = (2 + 4 + 6 + 8)/4 = 5।'),
    },
    revision: bi('Statistics is about making sense of data, not just computing numbers.', 'सांख्यिकी केवल गणना नहीं, बल्कि आँकड़ों को समझने के बारे में है।'),
  },
  'chapter-14-prob': {
    summary: bi('Covers basic probability as a measure of chance for equally likely outcomes.', 'समान संभाव्यता वाले परिणामों के लिए प्रायिकता मापने की मूल अवधारणा।'),
    focus: biList(['Identify sample space and favourable outcomes.', 'Use P(E) = favourable outcomes / total outcomes.', 'Connect probability with everyday uncertainty.']),
    example: {
      problem: bi('What is the probability of getting a head in one coin toss?', 'एक सिक्का उछालने पर हेड आने की प्रायिकता क्या है?'),
      solution: bi('There is 1 favourable outcome out of 2 equally likely outcomes, so probability = 1/2.', '2 समान संभाव्य परिणामों में से 1 अनुकूल, इसलिए प्रायिकता = 1/2।'),
    },
    revision: bi('Probability always lies between 0 and 1.', 'प्रायिकता हमेशा 0 और 1 के बीच होती है।'),
  },
};

const SCIENCE_HINTS = {
  'chapter-1-chem-reactions': {
    summary: 'Explains chemical changes, balancing equations, types of reactions and the idea of oxidation-reduction.',
    focus: [
      'Balance equations using atoms on both sides.',
      'Recognise combination, decomposition, displacement and double displacement reactions.',
      'Notice signs of chemical change such as gas, precipitate, temperature or colour change.',
    ],
    example: {
      problem: 'Balance: Fe + H2O -> Fe3O4 + H2',
      solution: '3Fe + 4H2O -> Fe3O4 + 4H2',
    },
    revision: 'A balanced equation obeys conservation of mass.',
  },
  'chapter-2-acids-bases': {
    summary: 'Covers acid-base indicators, pH, common salts and important daily-life uses of acids and bases.',
    focus: [
      'Differentiate acids and bases using indicators and pH.',
      'Remember neutralisation and common salt-based compounds.',
      'Link pH with soil, digestion and water quality applications.',
    ],
    example: {
      problem: 'What happens when an acid reacts with a base?',
      solution: 'They neutralise each other to form salt and water.',
    },
    revision: 'pH below 7 is acidic, pH above 7 is basic, and pH 7 is neutral.',
  },
  'chapter-3-metals': {
    summary: 'Builds the reactivity series, properties of metals and non-metals, and basic metallurgical ideas.',
    focus: [
      'Compare physical and chemical properties of metals and non-metals.',
      'Use reactivity trends to predict displacement reactions.',
      'Connect corrosion and its prevention with daily life.',
    ],
    example: {
      problem: 'Why are metals good conductors?',
      solution: 'They have free electrons that move easily through the lattice.',
    },
    revision: 'Metals tend to lose electrons; non-metals tend to gain them.',
  },
  'chapter-4-carbon': {
    summary: 'Introduces covalent bonding, homologous series, hydrocarbons and common carbon compounds.',
    focus: [
      'Understand why carbon forms long chains and rings.',
      'Distinguish saturated and unsaturated hydrocarbons.',
      'Recall soap, detergents, ethanol and acetic acid style applications.',
    ],
    example: {
      problem: 'Why is carbon versatile in organic chemistry?',
      solution: 'Carbon forms stable covalent bonds and catenation, so it can make chains, branches and rings.',
    },
    revision: 'Carbon forms four covalent bonds and is the backbone of organic chemistry.',
  },
  'chapter-5-life-processes': {
    summary: 'Covers nutrition, respiration, transport and excretion as the core life processes.',
    focus: [
      'Explain how food, oxygen and waste are managed in living organisms.',
      'Link breathing and respiration without confusing the two.',
      'Use diagrams for digestive and circulatory systems clearly.',
    ],
    example: {
      problem: 'Why is respiration necessary?',
      solution: 'It releases energy from food for all cellular activities.',
    },
    revision: 'Life processes keep the organism alive by supporting energy, transport and waste removal.',
  },
  'chapter-6-control': {
    summary: 'Explains nervous coordination, hormones, receptors and plant coordination in a school-friendly way.',
    focus: [
      'Differentiate reflex action and voluntary action.',
      'Remember major endocrine glands and their hormones.',
      'Connect plant movements with stimuli such as light and gravity.',
    ],
    example: {
      problem: 'Why do pupils contract in bright light?',
      solution: 'It is a reflex response controlled by the nervous system to protect the eye.',
    },
    revision: 'Control and coordination helps organisms respond quickly and effectively to their environment.',
  },
  'chapter-7-repro': {
    summary: 'Covers asexual and sexual reproduction, reproductive systems, and life cycle continuity.',
    focus: [
      'Distinguish reproduction from growth.',
      'Compare vegetative propagation, budding and binary fission with sexual reproduction.',
      'Understand human reproductive health basics in a safe, school-appropriate way.',
    ],
    example: {
      problem: 'Why is reproduction important?',
      solution: 'It ensures continuity of species across generations.',
    },
    revision: 'Reproduction is the biological process that produces new individuals of the same species.',
  },
  'chapter-8-heredity': {
    summary: 'Introduces inheritance, variation, Mendel-style reasoning and a very basic view of evolution.',
    focus: [
      'Use dominant and recessive traits in simple crosses.',
      'Notice how variation supports evolution over time.',
      'Link genes, traits and offspring characteristics carefully.',
    ],
    example: {
      problem: 'Why do children resemble their parents but are not identical?',
      solution: 'They inherit genes from both parents, but recombination and variation make each child unique.',
    },
    revision: 'Inheritance transfers traits; variation creates differences.',
  },
  'chapter-9-light': {
    summary: 'Explains reflection, refraction, image formation and the use of mirrors and lenses.',
    focus: [
      'Apply sign convention in ray diagrams.',
      'Relate object position to image nature and size.',
      'Read plane mirror, concave and convex mirror behaviour correctly.',
    ],
    example: {
      problem: 'What kind of image is formed by a plane mirror?',
      solution: 'Virtual, erect, same size and laterally inverted.',
    },
    revision: 'Ray diagrams are the fastest way to understand image formation.',
  },
  'chapter-10-eye': {
    summary: 'Covers the human eye, defects of vision and atmospheric effects that make the world colourful.',
    focus: [
      'Know the main parts of the eye and their functions.',
      'Understand myopia, hypermetropia and their correction.',
      'Connect scattering with sunrise, sunset and the blue sky.',
    ],
    example: {
      problem: 'How is myopia corrected?',
      solution: 'With a concave lens of suitable power.',
    },
    revision: 'The eye is an optical system that focuses images on the retina.',
  },
  'chapter-11-electricity': {
    summary: 'Introduces current, potential difference, Ohm\'s law, resistance and electric power.',
    focus: [
      'Use V = IR with consistent units.',
      'Compare series and parallel circuits.',
      'Relate heating effect and power in appliances.',
    ],
    example: {
      problem: 'If V = 10 V and R = 5 ohm, find current.',
      solution: 'I = V/R = 2 A.',
    },
    revision: 'Current is the rate of flow of charge.',
  },
  'chapter-12-magnetic': {
    summary: 'Covers magnetic fields, Fleming\'s rules, electromagnetism and practical devices like motors and generators.',
    focus: [
      'Draw field lines for bar magnets and current-carrying conductors.',
      'Use right-hand rules and Fleming\'s rules for direction problems.',
      'Relate magnetic effects to motors, bells and generators.',
    ],
    example: {
      problem: 'What is the direction of force on a current-carrying wire in a magnetic field?',
      solution: 'It is given by Fleming\'s left-hand rule.',
    },
    revision: 'Electric current and magnetism are tightly linked.',
  },
};

const PROGRAM_ORDER = [
  { classKey: 'class10', subjectKey: 'science' },
  { classKey: 'class10', subjectKey: 'mathematics' },
  { classKey: 'class9', subjectKey: 'science' },
  { classKey: 'class9', subjectKey: 'mathematics' },
  { classKey: 'class8', subjectKey: 'science' },
  { classKey: 'class8', subjectKey: 'mathematics' },
  { classKey: 'class7', subjectKey: 'science' },
  { classKey: 'class7', subjectKey: 'mathematics' },
  { classKey: 'class6', subjectKey: 'science' },
  { classKey: 'class6', subjectKey: 'mathematics' },
];

function slugify(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const CHAPTER_TITLE_HI = {
  'Real Numbers': 'वास्तविक संख्याएँ',
  Polynomials: 'बहुपद',
  'Pair of Linear Equations in Two Variables': 'दो चर वाले रैखिक समीकरणों का युग्म',
  'Quadratic Equations': 'द्विघात समीकरण',
  'Arithmetic Progressions': 'समान्तर श्रेणियाँ',
  Triangles: 'त्रिभुज',
  'Coordinate Geometry': 'निर्देशांक ज्यामिति',
  'Introduction to Trigonometry': 'त्रिकोणमिति का परिचय',
  'Some Applications of Trigonometry': 'त्रिकोणमिति के कुछ अनुप्रयोग',
  'Circles (Tangents)': 'वृत्त (स्पर्श रेखाएँ)',
  'Areas Related to Circles': 'वृत्तों से संबंधित क्षेत्रफल',
  'Surface Areas and Volumes': 'पृष्ठीय क्षेत्रफल और आयतन',
  Statistics: 'सांख्यिकी',
  Probability: 'प्रायिकता',
  'Chemical Reactions & Equations': 'रासायनिक अभिक्रियाएँ एवं समीकरण',
  'Acids, Bases and Salts': 'अम्ल, क्षार और लवण',
  'Metals and Non-Metals': 'धातु और अधातु',
  'Carbon and its Compounds': 'कार्बन और उसके यौगिक',
  'Life Processes': 'जीवन प्रक्रियाएँ',
  'Control and Coordination': 'नियंत्रण और समन्वय',
  'How do Organisms Reproduce?': 'जीव जनन कैसे करते हैं?',
  'Heredity and Evolution': 'आनुवंशिकता और विकास',
  'Light - Reflection and Refraction': 'प्रकाश - परावर्तन एवं अपवर्तन',
  'Human Eye and Colourful World': 'मानव नेत्र और रंगीन संसार',
  Electricity: 'विद्युत',
  'Magnetic Effects of Electric Current': 'विद्युत धारा के चुंबकीय प्रभाव',
};

const PROGRAM_CHAPTER_BLUEPRINTS = {
  'class10-science': [
    { id: 'chapter-1-chem-reactions', title: bi('Chemical Reactions & Equations', 'रासायनिक अभिक्रियाएँ एवं समीकरण') },
    { id: 'chapter-2-acids-bases', title: bi('Acids, Bases and Salts', 'अम्ल, क्षार और लवण') },
    { id: 'chapter-3-metals', title: bi('Metals and Non-Metals', 'धातु और अधातु') },
    { id: 'chapter-4-carbon', title: bi('Carbon and its Compounds', 'कार्बन और उसके यौगिक') },
    { id: 'chapter-5-life-processes', title: bi('Life Processes', 'जीवन प्रक्रियाएँ') },
    { id: 'chapter-6-control', title: bi('Control and Coordination', 'नियंत्रण और समन्वय') },
    { id: 'chapter-7-repro', title: bi('How do Organisms Reproduce?', 'जीव जनन कैसे करते हैं?') },
    { id: 'chapter-8-heredity', title: bi('Heredity and Evolution', 'आनुवंशिकता और विकास') },
    { id: 'chapter-9-light', title: bi('Light - Reflection and Refraction', 'प्रकाश - परावर्तन एवं अपवर्तन') },
    { id: 'chapter-10-eye', title: bi('Human Eye and Colourful World', 'मानव नेत्र और रंगीन संसार') },
    { id: 'chapter-11-electricity', title: bi('Electricity', 'विद्युत') },
    { id: 'chapter-12-magnetic', title: bi('Magnetic Effects of Electric Current', 'विद्युत धारा के चुंबकीय प्रभाव') },
  ],
  'class10-mathematics': [
    { id: 'chapter-1-real-numbers', title: bi('Real Numbers', 'वास्तविक संख्याएँ') },
    { id: 'chapter-2-polynomials', title: bi('Polynomials', 'बहुपद') },
    { id: 'chapter-3-linear-equations', title: bi('Pair of Linear Equations in Two Variables', 'दो चर वाले रैखिक समीकरणों का युग्म') },
    { id: 'chapter-4-quadratic-equations', title: bi('Quadratic Equations', 'द्विघात समीकरण') },
    { id: 'chapter-5-ap', title: bi('Arithmetic Progressions', 'समान्तर श्रेणियाँ') },
    { id: 'chapter-6-triangles', title: bi('Triangles', 'त्रिभुज') },
    { id: 'chapter-7-coordinate-geometry', title: bi('Coordinate Geometry', 'निर्देशांक ज्यामिति') },
    { id: 'chapter-8-trigonometry', title: bi('Introduction to Trigonometry', 'त्रिकोणमिति का परिचय') },
    { id: 'chapter-9-trig-applications', title: bi('Some Applications of Trigonometry', 'त्रिकोणमिति के कुछ अनुप्रयोग') },
    { id: 'chapter-10-circles', title: bi('Circles', 'वृत्त') },
    { id: 'chapter-11-areas', title: bi('Areas Related to Circles', 'वृत्तों से संबंधित क्षेत्रफल') },
    { id: 'chapter-12-surface-volumes', title: bi('Surface Areas and Volumes', 'पृष्ठीय क्षेत्रफल और आयतन') },
    { id: 'chapter-13-statistics', title: bi('Statistics', 'सांख्यिकी') },
    { id: 'chapter-14-probability', title: bi('Probability', 'प्रायिकता') },
  ],
  'class9-mathematics': [
    { id: 'chapter-1-number-systems', title: bi('Number Systems', 'संख्या पद्धति') },
    { id: 'chapter-2-polynomials', title: bi('Polynomials', 'बहुपद') },
    { id: 'chapter-3-coordinate-geometry', title: bi('Coordinate Geometry', 'निर्देशांक ज्यामिति') },
    { id: 'chapter-4-linear-equations', title: bi('Linear Equations in Two Variables', 'दो चर वाले रैखिक समीकरण') },
    { id: 'chapter-5-euclids-geometry', title: bi("Introduction to Euclid's Geometry", 'यूक्लिड ज्यामिति का परिचय') },
    { id: 'chapter-6-lines-angles', title: bi('Lines and Angles', 'रेखाएँ और कोण') },
    { id: 'chapter-7-triangles', title: bi('Triangles', 'त्रिभुज') },
    { id: 'chapter-8-quadrilaterals', title: bi('Quadrilaterals', 'चतुर्भुज') },
    { id: 'chapter-9-areas', title: bi('Areas of Parallelograms and Triangles', 'समांतर चतुर्भुज और त्रिभुजों के क्षेत्रफल') },
    { id: 'chapter-10-circles', title: bi('Circles', 'वृत्त') },
    { id: 'chapter-11-constructions', title: bi('Constructions', 'रचनाएँ') },
    { id: 'chapter-12-herons-formula', title: bi("Heron's Formula", 'हेरॉन का सूत्र') },
    { id: 'chapter-13-surface-volumes', title: bi('Surface Areas and Volumes', 'पृष्ठीय क्षेत्रफल और आयतन') },
    { id: 'chapter-14-statistics', title: bi('Statistics', 'सांख्यिकी') },
    { id: 'chapter-15-probability', title: bi('Probability', 'प्रायिकता') },
  ],
  'class9-science': [
    { id: 'chapter-1-matter-in-our-surroundings', title: bi('Matter in Our Surroundings', 'हमारे आस-पास के पदार्थ') },
    { id: 'chapter-2-is-matter-around-us-pure', title: bi('Is Matter Around Us Pure', 'क्या हमारे चारों ओर का पदार्थ शुद्ध है') },
    { id: 'chapter-3-atoms-and-molecules', title: bi('Atoms and Molecules', 'परमाणु और अणु') },
    { id: 'chapter-4-structure-of-the-atom', title: bi('Structure of the Atom', 'परमाणु की संरचना') },
    { id: 'chapter-5-fundamental-unit-of-life', title: bi('The Fundamental Unit of Life', 'जीवन की मौलिक इकाई') },
    { id: 'chapter-6-tissues', title: bi('Tissues', 'ऊतक') },
    { id: 'chapter-7-diversity-in-living-organisms', title: bi('Diversity in Living Organisms', 'जीवों में विविधता') },
    { id: 'chapter-8-motion', title: bi('Motion', 'गति') },
    { id: 'chapter-9-force-and-laws-of-motion', title: bi('Force and Laws of Motion', 'बल और गति के नियम') },
    { id: 'chapter-10-gravitation', title: bi('Gravitation', 'गुरुत्वाकर्षण') },
    { id: 'chapter-11-work-and-energy', title: bi('Work and Energy', 'कार्य और ऊर्जा') },
    { id: 'chapter-12-sound', title: bi('Sound', 'ध्वनि') },
    { id: 'chapter-13-why-do-we-fall-ill', title: bi('Why Do We Fall Ill', 'हम बीमार क्यों पड़ते हैं') },
    { id: 'chapter-14-natural-resources', title: bi('Natural Resources', 'प्राकृतिक संसाधन') },
    { id: 'chapter-15-improvement-in-food-resources', title: bi('Improvement in Food Resources', 'खाद्य संसाधनों में सुधार') },
  ],
  'class8-mathematics': [
    { id: 'chapter-1-rational-numbers', title: bi('Rational Numbers', 'परिमेय संख्याएँ') },
    { id: 'chapter-2-linear-equations', title: bi('Linear Equations in One Variable', 'एक चर वाले रैखिक समीकरण') },
    { id: 'chapter-3-quadrilaterals', title: bi('Understanding Quadrilaterals', 'चतुर्भुजों को समझना') },
    { id: 'chapter-4-practical-geometry', title: bi('Practical Geometry', 'व्यावहारिक ज्यामिति') },
    { id: 'chapter-5-data-handling', title: bi('Data Handling', 'आँकड़ों का प्रबंधन') },
    { id: 'chapter-6-squares-square-roots', title: bi('Squares and Square Roots', 'वर्ग और वर्गमूल') },
    { id: 'chapter-7-cubes-cube-roots', title: bi('Cubes and Cube Roots', 'घन और घनमूल') },
    { id: 'chapter-8-comparing-quantities', title: bi('Comparing Quantities', 'मात्राओं की तुलना') },
    { id: 'chapter-9-algebraic-expressions', title: bi('Algebraic Expressions and Identities', 'बीजीय व्यंजक और सर्वसमिकाएँ') },
    { id: 'chapter-10-visualising-solids', title: bi('Visualising Solid Shapes', 'ठोस आकृतियों का दृश्यांकन') },
    { id: 'chapter-11-mensuration', title: bi('Mensuration', 'क्षेत्रमिति') },
    { id: 'chapter-12-exponents-powers', title: bi('Exponents and Powers', 'घातांक और घात') },
    { id: 'chapter-13-direct-inverse-proportions', title: bi('Direct and Inverse Proportions', 'प्रत्यक्ष और व्युत्क्रमानुपाती') },
    { id: 'chapter-14-factorisation', title: bi('Factorisation', 'गुणनखंडन') },
    { id: 'chapter-15-introduction-to-graphs', title: bi('Introduction to Graphs', 'ग्राफ का परिचय') },
  ],
  'class8-science': [
    { id: 'chapter-1-crop-production', title: bi('Crop Production and Management', 'फसल उत्पादन और प्रबंधन') },
    { id: 'chapter-2-microorganisms', title: bi('Microorganisms: Friend and Foe', 'सूक्ष्मजीव: मित्र और शत्रु') },
    { id: 'chapter-3-synthetic-fibres', title: bi('Synthetic Fibres and Plastics', 'कृत्रिम तंतु और प्लास्टिक') },
    { id: 'chapter-4-metals-nonmetals', title: bi('Materials: Metals and Non-Metals', 'सामग्री: धातु और अधातु') },
    { id: 'chapter-5-coal-petroleum', title: bi('Coal and Petroleum', 'कोयला और पेट्रोलियम') },
    { id: 'chapter-6-combustion-flame', title: bi('Combustion and Flame', 'दहन और ज्वाला') },
    { id: 'chapter-7-conservation', title: bi('Conservation of Plants and Animals', 'पौधों और जंतुओं का संरक्षण') },
    { id: 'chapter-8-reproduction', title: bi('Reproduction in Animals', 'जंतुओं में जनन') },
    { id: 'chapter-9-adolescence', title: bi('Reaching the Age of Adolescence', 'किशोरावस्था की ओर') },
    { id: 'chapter-10-force-pressure', title: bi('Force and Pressure', 'बल और दाब') },
    { id: 'chapter-11-friction', title: bi('Friction', 'घर्षण') },
    { id: 'chapter-12-sound', title: bi('Sound', 'ध्वनि') },
    { id: 'chapter-13-chemical-effects', title: bi('Chemical Effects of Electric Current', 'विद्युत धारा के रासायनिक प्रभाव') },
    { id: 'chapter-14-natural-phenomena', title: bi('Some Natural Phenomena', 'कुछ प्राकृतिक परिघटनाएँ') },
    { id: 'chapter-15-light', title: bi('Light', 'प्रकाश') },
    { id: 'chapter-16-stars-solar-system', title: bi('Stars and the Solar System', 'तारे और सौर परिवार') },
    { id: 'chapter-17-pollution', title: bi('Pollution of Air and Water', 'वायु और जल का प्रदूषण') },
  ],
  'class7-mathematics': [
    { id: 'chapter-1-integers', title: bi('Integers', 'पूर्णांक') },
    { id: 'chapter-2-fractions-and-decimals', title: bi('Fractions and Decimals', 'भिन्न और दशमलव') },
    { id: 'chapter-3-data-handling', title: bi('Data Handling', 'आँकड़ों का प्रबंधन') },
    { id: 'chapter-4-simple-equations', title: bi('Simple Equations', 'सरल समीकरण') },
    { id: 'chapter-5-lines-and-angles', title: bi('Lines and Angles', 'रेखाएँ और कोण') },
    { id: 'chapter-6-triangle-and-its-properties', title: bi('The Triangle and its Properties', 'त्रिभुज और उसके गुण') },
    { id: 'chapter-7-congruence-of-triangles', title: bi('Congruence of Triangles', 'त्रिभुजों की सर्वांगसमता') },
    { id: 'chapter-8-comparing-quantities', title: bi('Comparing Quantities', 'मात्राओं की तुलना') },
    { id: 'chapter-9-rational-numbers', title: bi('Rational Numbers', 'परिमेय संख्याएँ') },
    { id: 'chapter-10-practical-geometry', title: bi('Practical Geometry', 'व्यावहारिक ज्यामिति') },
    { id: 'chapter-11-perimeter-and-area', title: bi('Perimeter and Area', 'परिमाप और क्षेत्रफल') },
    { id: 'chapter-12-algebraic-expressions', title: bi('Algebraic Expressions', 'बीजीय व्यंजक') },
    { id: 'chapter-13-exponents-and-powers', title: bi('Exponents and Powers', 'घातांक और घात') },
    { id: 'chapter-14-symmetry', title: bi('Symmetry', 'सममिति') },
    { id: 'chapter-15-visualising-solid-shapes', title: bi('Visualising Solid Shapes', 'ठोस आकृतियों का दृश्यांकन') },
  ],
  'class7-science': [
    { id: 'chapter-1-nutrition-in-plants', title: bi('Nutrition in Plants', 'पौधों में पोषण') },
    { id: 'chapter-2-nutrition-in-animals', title: bi('Nutrition in Animals', 'जंतुओं में पोषण') },
    { id: 'chapter-3-fibre-to-fabric', title: bi('Fibre to Fabric', 'रेशे से वस्त्र तक') },
    { id: 'chapter-4-heat', title: bi('Heat', 'ऊष्मा') },
    { id: 'chapter-5-acids-bases-salts', title: bi('Acids, Bases and Salts', 'अम्ल, क्षार और लवण') },
    { id: 'chapter-6-physical-chemical-changes', title: bi('Physical and Chemical Changes', 'भौतिक और रासायनिक परिवर्तन') },
    { id: 'chapter-7-weather-climate-adaptations', title: bi('Weather, Climate and Adaptations', 'मौसम, जलवायु और अनुकूलन') },
    { id: 'chapter-8-winds-storms-cyclones', title: bi('Winds, Storms and Cyclones', 'पवन, तूफान और चक्रवात') },
    { id: 'chapter-9-soil', title: bi('Soil', 'मृदा') },
    { id: 'chapter-10-respiration-in-organisms', title: bi('Respiration in Organisms', 'जीवों में श्वसन') },
    { id: 'chapter-11-transportation-in-animals-plants', title: bi('Transportation in Animals and Plants', 'जंतुओं और पौधों में परिवहन') },
    { id: 'chapter-12-reproduction-in-plants', title: bi('Reproduction in Plants', 'पौधों में जनन') },
    { id: 'chapter-13-motion-and-time', title: bi('Motion and Time', 'गति और समय') },
    { id: 'chapter-14-electric-current-and-its-effects', title: bi('Electric Current and its Effects', 'विद्युत धारा और इसके प्रभाव') },
    { id: 'chapter-15-light', title: bi('Light', 'प्रकाश') },
    { id: 'chapter-16-water-a-precious-resource', title: bi('Water: A Precious Resource', 'जल: एक बहुमूल्य संसाधन') },
    { id: 'chapter-17-forests-our-lifeline', title: bi('Forests: Our Lifeline', 'वन: हमारी जीवन रेखा') },
    { id: 'chapter-18-wastewater-story', title: bi('Wastewater Story', 'अपशिष्ट जल की कहानी') },
  ],
  'class6-mathematics': [
    { id: 'chapter-1-knowing-our-numbers', title: bi('Knowing Our Numbers', 'संख्याओं की जानकारी') },
    { id: 'chapter-2-whole-numbers', title: bi('Whole Numbers', 'पूर्ण संख्याएँ') },
    { id: 'chapter-3-playing-with-numbers', title: bi('Playing with Numbers', 'संख्याओं के साथ खेलना') },
    { id: 'chapter-4-basic-geometrical-ideas', title: bi('Basic Geometrical Ideas', 'मूलभूत ज्यामितीय अवधारणाएँ') },
    { id: 'chapter-5-understanding-elementary-shapes', title: bi('Understanding Elementary Shapes', 'प्रारंभिक आकृतियों को समझना') },
    { id: 'chapter-6-integers', title: bi('Integers', 'पूर्णांक') },
    { id: 'chapter-7-fractions', title: bi('Fractions', 'भिन्न') },
    { id: 'chapter-8-decimals', title: bi('Decimals', 'दशमलव') },
    { id: 'chapter-9-data-handling', title: bi('Data Handling', 'आँकड़ों का प्रबंधन') },
    { id: 'chapter-10-mensuration', title: bi('Mensuration', 'क्षेत्रमिति') },
    { id: 'chapter-11-algebra', title: bi('Algebra', 'बीजगणित') },
    { id: 'chapter-12-ratio-and-proportion', title: bi('Ratio and Proportion', 'अनुपात और समानुपात') },
    { id: 'chapter-13-symmetry', title: bi('Symmetry', 'सममिति') },
    { id: 'chapter-14-practical-geometry', title: bi('Practical Geometry', 'व्यावहारिक ज्यामिति') },
  ],
  'class6-science': [
    { id: 'chapter-1-food-where-does-it-come-from', title: bi('Food: Where Does It Come From?', 'भोजन: यह कहाँ से आता है?') },
    { id: 'chapter-2-components-of-food', title: bi('Components of Food', 'भोजन के घटक') },
    { id: 'chapter-3-fibre-to-fabric', title: bi('Fibre to Fabric', 'रेशे से वस्त्र तक') },
    { id: 'chapter-4-sorting-materials-into-groups', title: bi('Sorting Materials into Groups', 'पदार्थों को समूहों में बाँटना') },
    { id: 'chapter-5-separation-of-substances', title: bi('Separation of Substances', 'पदार्थों का पृथक्करण') },
    { id: 'chapter-6-changes-around-us', title: bi('Changes Around Us', 'हमारे आसपास परिवर्तन') },
    { id: 'chapter-7-getting-to-know-plants', title: bi('Getting to Know Plants', 'पौधों को जानना') },
    { id: 'chapter-8-body-movements', title: bi('Body Movements', 'शरीर की गति') },
    { id: 'chapter-9-living-organisms-and-surroundings', title: bi('The Living Organisms and Their Surroundings', 'जीवित प्राणी और उनके परिवेश') },
    { id: 'chapter-10-motion-and-measurement-of-distances', title: bi('Motion and Measurement of Distances', 'गति और दूरी का मापन') },
    { id: 'chapter-11-light-shadows-and-reflections', title: bi('Light, Shadows and Reflections', 'प्रकाश, छाया और परावर्तन') },
    { id: 'chapter-12-electricity-and-circuits', title: bi('Electricity and Circuits', 'विद्युत और परिपथ') },
    { id: 'chapter-13-fun-with-magnets', title: bi('Fun with Magnets', 'चुंबकों के साथ मज़ा') },
    { id: 'chapter-14-water', title: bi('Water', 'जल') },
    { id: 'chapter-15-air-around-us', title: bi('Air Around Us', 'हमारे आसपास वायु') },
    { id: 'chapter-16-garbage-in-garbage-out', title: bi('Garbage In, Garbage Out', 'कूड़ा-करकट: अन्दर, बाहर') },
  ],
};

function toChapterTitle(title) {
  return bi(title, CHAPTER_TITLE_HI[title] || title);
}

function humanizeChapterId(id) {
  return id
    .replace(/^chapter-\d+-/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getProgramBlueprints(programKey, catalog) {
  const blueprints = PROGRAM_CHAPTER_BLUEPRINTS[programKey];
  if (blueprints && blueprints.length > 0) return blueprints;

  return catalog.map((item) => ({
    id: item.id,
    title: bi(humanizeChapterId(item.id), humanizeChapterId(item.id)),
  }));
}

function buildMathHintFromTitle(title, classLabel) {
  const english = resolveText(title, 'english');
  const titleHi = resolveText(title, 'hindi');
  switch (english) {
    case 'Real Numbers':
      return {
        summary: bi(`Builds the number system for ${classLabel} using rational and irrational numbers.`, `${classLabel} के लिए परिमेय और अपरिमेय संख्याओं के माध्यम से संख्या प्रणाली का निर्माण करता है।`),
        focus: biList(['Classify numbers and decimal expansions.', 'Use HCF/LCM ideas and Euclid\'s algorithm.', 'Recognise rational vs irrational numbers quickly.']),
        example: { problem: bi('Find the HCF of 84 and 126.', '84 और 126 का HCF ज्ञात कीजिए।'), solution: bi('HCF = 42.', 'HCF = 42।') },
        revision: bi('Rational numbers terminate or repeat; irrational numbers do not.', 'परिमेय संख्याएँ समाप्त होती हैं या दोहराती हैं; अपरिमेय नहीं।'),
      };
    case 'Polynomials':
      return {
        summary: bi(`Introduces algebraic expressions and zeroes for ${classLabel} level algebra.`, `${classLabel} स्तर की बीजगणित में बीजीय व्यंजक और शून्य को समझाता है।`),
        focus: biList(['Know degree, coefficients and zeroes.', 'Relate graph intersections to zeroes.', 'Use factorisation in simple cases.']),
        example: { problem: bi('Find the zeroes of x^2 - 5x + 6.', 'x^2 - 5x + 6 के शून्य ज्ञात कीजिए।'), solution: bi('Zeroes are 2 and 3.', 'शून्य 2 और 3 हैं।') },
        revision: bi('A polynomial can have at most as many zeroes as its degree.', 'एक बहुपद के शून्य उसके घात के अधिकतम बराबर होते हैं।'),
      };
    case 'Pair of Linear Equations in Two Variables':
      return {
        summary: bi('Covers two-variable linear systems and their graph meaning.', 'दो चर वाले रैखिक समीकरण और उनके ग्राफिक अर्थ को समझाता है।'),
        focus: biList(['Solve by elimination and substitution.', 'Use coefficient ratios to classify the system.', 'Read intersecting, parallel and coincident lines.']),
        example: { problem: bi('Solve 2x + y = 7 and x - y = 1.', '2x + y = 7 और x - y = 1 को हल कीजिए।'), solution: bi('x = 8/3, y = 5/3.', 'x = 8/3, y = 5/3।') },
        revision: bi('One solution means intersecting lines.', 'एक समाधान का अर्थ प्रतिच्छेद करने वाली रेखाएँ हैं।'),
      };
    case 'Quadratic Equations':
      return {
        summary: bi('Builds quadratic equations, roots and the discriminant test.', 'द्विघात समीकरण, मूल और विविक्तकर परीक्षण को समझाता है।'),
        focus: biList(['Write equations in ax^2 + bx + c = 0 form.', 'Use factorisation or formula.', 'Interpret D to know the nature of roots.']),
        example: { problem: bi('Solve x^2 - 7x + 12 = 0.', 'x^2 - 7x + 12 = 0 को हल कीजिए।'), solution: bi('x = 3 or 4.', 'x = 3 या 4।') },
        revision: bi('D > 0 distinct roots, D = 0 equal roots, D < 0 no real roots.', 'D > 0 भिन्न मूल, D = 0 समान मूल, D < 0 कोई वास्तविक मूल नहीं।'),
      };
    case 'Arithmetic Progressions':
      return {
        summary: bi('Introduces sequences with a fixed difference between terms.', 'स्थिर अंतर वाली श्रेणी को समझाता है।'),
        focus: biList(['Identify a and d.', 'Use nth term and sum formula.', 'Solve sequence-based board questions.']),
        example: { problem: bi('Find the 10th term of 3, 7, 11, 15, ...', '3, 7, 11, 15, ... का 10वाँ पद ज्ञात कीजिए।'), solution: bi('10th term = 39.', '10वाँ पद = 39।') },
        revision: bi('AP has constant common difference.', 'AP में समान अंतर स्थिर रहता है।'),
      };
    case 'Triangles':
      return {
        summary: bi('Focuses on similarity and proportional reasoning in triangles.', 'त्रिभुजों में समरूपता और समानुपात को समझाता है।'),
        focus: biList(['Use similarity criteria.', 'Apply BPT and area ratios.', 'Link corresponding sides clearly.']),
        example: { problem: bi('Side ratio 2:3 gives what area ratio?', '2:3 के पक्ष अनुपात पर क्षेत्रफल अनुपात क्या होगा?'), solution: bi('4:9.', '4:9।') },
        revision: bi('Similar triangles have same shape, not necessarily same size.', 'समरूप त्रिभुजों का आकार समान होता है, आकार नहीं।'),
      };
    case 'Coordinate Geometry':
      return {
        summary: bi('Introduces the coordinate plane and plotting points.', 'निर्देशांक तल और बिंदु आलेखन को समझाता है।'),
        focus: biList(['Locate points in quadrants.', 'Read x and y coordinates carefully.', 'Use coordinates in geometry problems.']),
        example: { problem: bi('Plot (-3, 4).', '(-3, 4) को आलेखित कीजिए।'), solution: bi('Quadrant II.', 'द्वितीय चतुर्थांश।') },
        revision: bi('x is horizontal, y is vertical.', 'x क्षैतिज, y ऊर्ध्वाधर निर्देशांक है।'),
      };
    case 'Introduction to Trigonometry':
      return {
        summary: bi('Introduces basic trigonometric ratios for right triangles.', 'समकोण त्रिभुज के लिए मूल त्रिकोणमितीय अनुपात समझाता है।'),
        focus: biList(['Remember sin, cos, tan.', 'Learn standard angles.', 'Use complementary angle relations.']),
        example: { problem: bi('If sin A = 3/5, find cos A.', 'यदि sin A = 3/5 है, तो cos A ज्ञात कीजिए।'), solution: bi('cos A = 4/5.', 'cos A = 4/5।') },
        revision: bi('Trigonometry links angle and sides.', 'त्रिकोणमिति कोण और भुजाओं को जोड़ती है।'),
      };
    case 'Some Applications of Trigonometry':
      return {
        summary: bi('Applies trigonometry to heights and distances.', 'ऊँचाई और दूरी पर त्रिकोणमिति का अनुप्रयोग।'),
        focus: biList(['Draw the right triangle first.', 'Pick the right ratio.', 'Track units and angles carefully.']),
        example: { problem: bi('A tower seen at 45° from 20 m away. Find height.', '20 m दूर से 45° पर दिखने वाले टावर की ऊँचाई ज्ञात कीजिए।'), solution: bi('Height = 20 m.', 'ऊँचाई = 20 m।') },
        revision: bi('Diagram first, formula second.', 'पहले चित्र, फिर सूत्र।'),
      };
    case 'Circles':
      return {
        summary: bi('Introduces tangents and basic circle theorems.', 'स्पर्श रेखाएँ और वृत्त के मूल प्रमेय समझाता है।'),
        focus: biList(['A tangent touches once.', 'Radius is perpendicular to tangent.', 'Tangents from an external point are equal.']),
        example: { problem: bi('Compare AP and AQ from same external point.', 'एक ही बाह्य बिंदु से AP और AQ की तुलना कीजिए।'), solution: bi('AP = AQ.', 'AP = AQ।') },
        revision: bi('Tangent-radius property is key.', 'स्पर्शरेखा-त्रिज्या संबंध महत्वपूर्ण है।'),
      };
    case 'Constructions':
      return {
        summary: bi('Covers ruler-compass constructions from school geometry.', 'पैमाना-परकार निर्माण को समझाता है।'),
        focus: biList(['Follow steps exactly.', 'Keep arcs neat and labelled.', 'Use compass radius carefully.']),
        example: { problem: bi('Construct a triangle with given sides.', 'दिए गए पक्षों से त्रिभुज बनाइए।'), solution: bi('Draw base, mark arcs, join points.', 'आधार रेखा खींचें, चाप लें, बिंदु जोड़ें।') },
        revision: bi('Construction questions need neat steps and labels.', 'रचना प्रश्नों में साफ चरण और लेबल आवश्यक हैं।'),
      };
    case 'Areas Related to Circles':
      return {
        summary: bi('Focuses on sector and segment area calculations.', 'सेक्टर और खंड के क्षेत्रफल पर केंद्रित है।'),
        focus: biList(['Know area of sector.', 'Use angle proportion.', 'Break shapes into parts.']),
        example: { problem: bi('Area of 90° sector of radius 7 cm.', '7 cm त्रिज्या वाले 90° सेक्टर का क्षेत्रफल ज्ञात कीजिए।'), solution: bi('49pi/4 cm^2.', '49pi/4 cm^2।') },
        revision: bi('Sector area is proportional to angle.', 'सेक्टर का क्षेत्रफल कोण के समानुपाती होता है।'),
      };
    case 'Surface Areas and Volumes':
      return {
        summary: bi('Covers standard area and volume formulae for solids.', 'ठोसों के क्षेत्रफल और आयतन के सूत्रों को समझाता है।'),
        focus: biList(['Memorise formulas.', 'Use consistent units.', 'Check whether total or curved surface area is asked.']),
        example: { problem: bi('Volume of cuboid 3 x 4 x 5 cm.', '3 x 4 x 5 cm घनाभ का आयतन ज्ञात कीजिए।'), solution: bi('60 cm^3.', '60 cm^3।') },
        revision: bi('Volume is in cubic units.', 'आयतन घन इकाइयों में होता है।'),
      };
    case 'Statistics':
      return {
        summary: bi('Introduces mean, median, mode and data interpretation.', 'माध्य, माध्यिका, बहुलक और आँकड़ों की व्याख्या समझाता है।'),
        focus: biList(['Organise data clearly.', 'Use the right average.', 'Read graphs correctly.']),
        example: { problem: bi('Find mean of 2, 4, 6, 8.', '2, 4, 6, 8 का माध्य ज्ञात कीजिए।'), solution: bi('Mean = 5.', 'माध्य = 5।') },
        revision: bi('Statistics helps organise information.', 'सांख्यिकी सूचना को व्यवस्थित करने में मदद करती है।'),
      };
    case 'Probability':
      return {
        summary: bi('Introduces chance for equally likely outcomes.', 'समान संभाव्यता वाले परिणामों की प्रायिकता समझाता है।'),
        focus: biList(['Sample space first.', 'Favourable outcomes next.', 'Probability = favourable / total.']),
        example: { problem: bi('Probability of head in one coin toss.', 'एक सिक्का उछालने पर हेड की प्रायिकता क्या है?'), solution: bi('1/2.', '1/2।') },
        revision: bi('Probability lies between 0 and 1.', 'प्रायिकता 0 और 1 के बीच होती है।'),
      };
    // ── Class 9 specific titles ────────────────────────────────────────────
    case 'Number Systems':
      return {
        summary: bi('Covers natural, whole, integer, rational and irrational numbers and decimal expansions.', 'प्राकृत, पूर्ण, पूर्णांक, परिमेय और अपरिमेय संख्याएँ तथा दशमलव विस्तार को समझाता है।'),
        focus: biList(['Place every number on the number line.', 'Represent surds as points on the number line.', 'Use laws of exponents for real numbers.']),
        example: { problem: bi('Represent √2 on the number line.', '√2 को संख्या रेखा पर दर्शाइए।'), solution: bi('Construct a right triangle with legs 1 and 1; hypotenuse = √2.', '1 और 1 भुजाओं वाला समकोण त्रिभुज बनाएँ; कर्ण = √2।') },
        revision: bi('Irrational numbers cannot be written as p/q; their decimal expansion is non-terminating non-repeating.', 'अपरिमेय संख्याएँ p/q रूप में नहीं लिखी जा सकतीं; इनका दशमलव विस्तार अनंत और अपुनरावर्ती होता है।'),
      };
    case 'Linear Equations in Two Variables':
      return {
        summary: bi('Explores solutions of ax + by + c = 0 and their geometric meaning as a line.', 'ax + by + c = 0 के हल और उनके रेखा के रूप में ज्यामितीय अर्थ को समझाता है।'),
        focus: biList(['A linear equation in two variables has infinitely many solutions.', 'Every solution is a point on the line representing the equation.', 'Verify whether a given point satisfies the equation.']),
        example: { problem: bi('Find three solutions of 2x + y = 6.', '2x + y = 6 के तीन हल ज्ञात कीजिए।'), solution: bi('(0,6), (1,4), (3,0).', '(0,6), (1,4), (3,0)।') },
        revision: bi('The graph of a linear equation in two variables is always a straight line.', 'दो चर वाले रैखिक समीकरण का ग्राफ सदैव एक सीधी रेखा होती है।'),
      };
    case "Introduction to Euclid's Geometry":
      return {
        summary: bi("Introduces Euclid's postulates and axioms as the foundation of geometry.", 'यूक्लिड के अभिगृहीतों और स्वयंसिद्धों को ज्यामिति की नींव के रूप में समझाता है।'),
        focus: biList(["Know the five Euclid's postulates.", 'Distinguish an axiom from a postulate.', 'Apply the first postulate in simple proofs.']),
        example: { problem: bi("State Euclid's first postulate.", 'यूक्लिड का प्रथम अभिगृहीत लिखिए।'), solution: bi('A straight line can be drawn from any one point to any other point.', 'किन्हीं दो बिंदुओं को मिलाने वाली एक सीधी रेखा खींची जा सकती है।') },
        revision: bi('Axioms are universally accepted; postulates are specific to geometry.', 'स्वयंसिद्ध सार्वत्रिक रूप से मान्य हैं; अभिगृहीत ज्यामिति के लिए विशेष हैं।'),
      };
    case 'Lines and Angles':
      return {
        summary: bi('Covers types of angles, pairs of angles and properties of parallel lines.', 'कोणों के प्रकार, कोणों के युग्म और समांतर रेखाओं के गुणधर्म को समझाता है।'),
        focus: biList(['Know complementary, supplementary and vertically opposite angles.', 'Use alternate and co-interior angles on parallel lines.', 'Apply the angle sum property of a triangle.']),
        example: { problem: bi('If two lines are parallel and a transversal cuts them, find the alternate angles.', 'यदि दो रेखाएँ समांतर हों और एक तिर्यक रेखा उन्हें काटे, तो एकांतर कोण ज्ञात कीजिए।'), solution: bi('Alternate interior angles are equal.', 'एकांतर अभ्यंतर कोण बराबर होते हैं।') },
        revision: bi('The sum of angles on a straight line is 180°.', 'एक सीधी रेखा पर बने कोणों का योग 180° होता है।'),
      };
    case 'Quadrilaterals':
      return {
        summary: bi('Covers properties of quadrilaterals, especially parallelograms.', 'चतुर्भुजों, विशेष रूप से समांतर चतुर्भुजों के गुणधर्मों को समझाता है।'),
        focus: biList(['Sum of interior angles of a quadrilateral = 360°.', 'Know all properties of parallelogram, rhombus, rectangle and square.', 'Use the midpoint theorem in proofs.']),
        example: { problem: bi('Find the fourth angle of a quadrilateral with angles 70°, 80°, 100°.', '70°, 80°, 100° कोण वाले चतुर्भुज का चौथा कोण ज्ञात कीजिए।'), solution: bi('Fourth angle = 360 - 250 = 110°.', 'चौथा कोण = 360 - 250 = 110°।') },
        revision: bi('In a parallelogram, opposite sides are equal and opposite angles are equal.', 'समांतर चतुर्भुज में सम्मुख भुजाएँ और सम्मुख कोण बराबर होते हैं।'),
      };
    case 'Areas of Parallelograms and Triangles':
      return {
        summary: bi('Shows that figures on the same base and between the same parallels have equal area.', 'समान आधार और समान समांतर रेखाओं के बीच की आकृतियों का क्षेत्रफल बराबर होता है।'),
        focus: biList(['Area of parallelogram = base × height.', 'Triangle area is half that of a parallelogram on the same base.', 'Apply the theorem to prove equal area results.']),
        example: { problem: bi('A parallelogram and a triangle are on the same base and between the same parallels. How do their areas compare?', 'एक समांतर चतुर्भुज और त्रिभुज समान आधार पर और समान समांतर रेखाओं के बीच हैं। क्षेत्रफल का क्या संबंध है?'), solution: bi('Triangle area = half the parallelogram area.', 'त्रिभुज का क्षेत्रफल = समांतर चतुर्भुज के क्षेत्रफल का आधा।') },
        revision: bi('Figures between same parallels on equal bases have equal areas.', 'समान समांतर रेखाओं के बीच समान आधारों पर बनी आकृतियों का क्षेत्रफल बराबर होता है।'),
      };
    case "Heron's Formula":
      return {
        summary: bi("Introduces Heron's formula to find the area of a triangle using side lengths only.", 'केवल भुजाओं की लंबाई से त्रिभुज का क्षेत्रफल ज्ञात करने के लिए हेरॉन के सूत्र को समझाता है।'),
        focus: biList(['Calculate s = (a + b + c)/2.', "Use Heron's formula: Area = √(s(s-a)(s-b)(s-c)).", 'Apply to triangles and quadrilaterals.']),
        example: { problem: bi('Find area of a triangle with sides 3, 4 and 5 cm.', '3, 4 और 5 cm भुजाओं वाले त्रिभुज का क्षेत्रफल ज्ञात कीजिए।'), solution: bi('s = 6; Area = √(6×3×2×1) = 6 cm².', 's = 6; क्षेत्रफल = √(6×3×2×1) = 6 cm²।') },
        revision: bi("Heron's formula works for any triangle when all three sides are known.", 'हेरॉन का सूत्र किसी भी त्रिभुज के लिए काम करता है जब तीनों भुजाएँ ज्ञात हों।'),
      };
    // ── Class 8 specific titles ────────────────────────────────────────────
    case 'Rational Numbers':
      return {
        summary: bi('Covers properties of rational numbers including closure, commutativity and associativity.', 'परिमेय संख्याओं के संवृत्तता, क्रमविनिमय और साहचर्य जैसे गुणधर्मों को समझाता है।'),
        focus: biList(['Write rationals in p/q form.', 'Apply operations: addition, subtraction, multiplication and division.', 'Represent rational numbers on the number line.']),
        example: { problem: bi('Find a rational number between 1/3 and 1/2.', '1/3 और 1/2 के बीच एक परिमेय संख्या ज्ञात कीजिए।'), solution: bi('5/12 lies between them.', '5/12 उनके बीच है।') },
        revision: bi('Rational numbers are dense; infinitely many rationals exist between any two rationals.', 'परिमेय संख्याएँ सघन होती हैं; किन्हीं दो परिमेय संख्याओं के बीच अनंत परिमेय संख्याएँ होती हैं।'),
      };
    case 'Linear Equations in One Variable':
      return {
        summary: bi('Solves equations of the form ax + b = c using inverse operations.', 'ax + b = c रूप के समीकरण व्युत्क्रम संक्रियाओं से हल करना सिखाता है।'),
        focus: biList(['Isolate the variable on one side.', 'Use transposing to simplify equations.', 'Form equations from word problems.']),
        example: { problem: bi('Solve 3x + 5 = 14.', '3x + 5 = 14 को हल कीजिए।'), solution: bi('3x = 9, so x = 3.', '3x = 9, इसलिए x = 3।') },
        revision: bi('A linear equation in one variable has exactly one solution.', 'एक चर वाले रैखिक समीकरण का ठीक एक हल होता है।'),
      };
    case 'Understanding Quadrilaterals':
      return {
        summary: bi('Classifies polygons and explores properties of quadrilaterals like parallelogram, trapezium and rhombus.', 'बहुभुजों का वर्गीकरण और समांतर चतुर्भुज, समलंब चतुर्भुज, समचतुर्भुज के गुणधर्म समझाता है।'),
        focus: biList(['Sum of exterior angles of any polygon = 360°.', 'Know defining properties of each quadrilateral type.', 'Distinguish between parallelogram and rectangle.']),
        example: { problem: bi('Find the sum of interior angles of a hexagon.', 'षट्भुज के अंतः कोणों का योग ज्ञात कीजिए।'), solution: bi('(6-2) × 180 = 720°.', '(6-2) × 180 = 720°।') },
        revision: bi('Sum of interior angles of an n-sided polygon = (n-2) × 180°.', 'n भुजाओं वाले बहुभुज के अंतः कोणों का योग = (n-2) × 180°।'),
      };
    case 'Practical Geometry':
      return {
        summary: bi('Teaches construction of quadrilaterals and other shapes with compass and ruler.', 'कम्पास और स्केल से चतुर्भुजों और अन्य आकृतियों का निर्माण सिखाता है।'),
        focus: biList(['Identify minimum data needed for a unique construction.', 'Follow steps precisely with compass and ruler.', 'Check the construction by measurement.']),
        example: { problem: bi('Construct a quadrilateral with four sides and one diagonal given.', 'चार भुजाओं और एक विकर्ण के साथ एक चतुर्भुज बनाइए।'), solution: bi('Draw the diagonal first and use it as a base for two triangles.', 'पहले विकर्ण खींचें और उसे दो त्रिभुजों के आधार के रूप में उपयोग करें।') },
        revision: bi('A quadrilateral is uniquely determined by 5 independent measurements.', 'एक चतुर्भुज 5 स्वतंत्र मापों से विशिष्ट रूप से निर्धारित होता है।'),
      };
    case 'Data Handling':
      return {
        summary: bi('Introduces frequency tables, bar graphs, pie charts, probability and random experiments.', 'बारंबारता सारणी, दंड आरेख, पाई चार्ट, प्रायिकता और यादृच्छिक प्रयोगों को समझाता है।'),
        focus: biList(['Organise data in frequency tables.', 'Draw and interpret bar graphs and pie charts.', 'Find basic probability for equally likely events.']),
        example: { problem: bi('Draw a pie chart for a data set with 4 sectors.', '4 क्षेत्रों वाले डेटा के लिए पाई चार्ट बनाइए।'), solution: bi('Convert each value to degrees: (value/total) × 360.', 'प्रत्येक मान को डिग्री में बदलें: (मान/कुल) × 360।') },
        revision: bi('A pie chart represents parts of a whole using angles.', 'पाई चार्ट कोणों का उपयोग करके संपूर्ण के भागों को दर्शाता है।'),
      };
    case 'Squares and Square Roots':
      return {
        summary: bi('Covers perfect squares, square roots by prime factorisation and long division.', 'पूर्ण वर्ग, अभाज्य गुणनखंड और भाग विधि से वर्गमूल को समझाता है।'),
        focus: biList(['Identify perfect squares up to 100.', 'Find square root using prime factorisation.', 'Use long division for larger numbers.']),
        example: { problem: bi('Find √144 by prime factorisation.', 'अभाज्य गुणनखंड विधि से √144 ज्ञात कीजिए।'), solution: bi('144 = 2^4 × 3^2, so √144 = 12.', '144 = 2^4 × 3^2, इसलिए √144 = 12।') },
        revision: bi('A perfect square has an even number of prime factors for each prime.', 'पूर्ण वर्ग में प्रत्येक अभाज्य गुणनखंड की संख्या सम होती है।'),
      };
    case 'Cubes and Cube Roots':
      return {
        summary: bi('Introduces perfect cubes and cube roots by prime factorisation.', 'पूर्ण घन और अभाज्य गुणनखंड विधि से घनमूल को समझाता है।'),
        focus: biList(['Know cubes of 1 to 10.', 'Find cube root using prime factorisation.', 'Check if a number is a perfect cube.']),
        example: { problem: bi('Find the cube root of 27000.', '27000 का घनमूल ज्ञात कीजिए।'), solution: bi('27000 = 27 × 1000 = 3^3 × 10^3, so cube root = 30.', '27000 = 27 × 1000 = 3^3 × 10^3, इसलिए घनमूल = 30।') },
        revision: bi('A perfect cube has prime factors each appearing a multiple of 3 times.', 'पूर्ण घन में प्रत्येक अभाज्य गुणनखंड 3 के गुणज बार आता है।'),
      };
    case 'Comparing Quantities':
      return {
        summary: bi('Covers ratios, percentages, profit and loss, simple and compound interest.', 'अनुपात, प्रतिशत, लाभ-हानि, सरल और चक्रवृद्धि ब्याज को समझाता है।'),
        focus: biList(['Convert fractions and decimals to percentages.', 'Use profit = SP - CP and loss = CP - SP.', 'Apply compound interest formula.']),
        example: { problem: bi('Find the compound interest on ₹1000 at 10% per annum for 2 years.', '₹1000 पर 10% वार्षिक दर से 2 वर्षों का चक्रवृद्धि ब्याज ज्ञात कीजिए।'), solution: bi('A = 1000 × (1.1)^2 = 1210, so CI = ₹210.', 'A = 1000 × (1.1)^2 = 1210, इसलिए CI = ₹210।') },
        revision: bi('Compound interest grows faster than simple interest over the same period.', 'चक्रवृद्धि ब्याज एक ही अवधि में सरल ब्याज से अधिक तेज़ी से बढ़ता है।'),
      };
    case 'Algebraic Expressions and Identities':
      return {
        summary: bi('Introduces polynomial expressions, standard identities and their applications.', 'बहुपद व्यंजक, मानक सर्वसमिकाएँ और उनके अनुप्रयोगों को समझाता है।'),
        focus: biList(['Know (a+b)^2, (a-b)^2 and (a+b)(a-b) identities.', 'Multiply polynomials using the distributive law.', 'Use identities to simplify calculations.']),
        example: { problem: bi('Expand (3x + 2y)^2.', '(3x + 2y)^2 का विस्तार कीजिए।'), solution: bi('9x^2 + 12xy + 4y^2.', '9x^2 + 12xy + 4y^2।') },
        revision: bi('Algebraic identities are equalities that hold for all values of the variables.', 'बीजीय सर्वसमिकाएँ सभी चर मानों के लिए सत्य होती हैं।'),
      };
    case 'Mensuration':
      return {
        summary: bi('Covers perimeter and area of plane figures and surface area and volume of 3D shapes.', '2D आकृतियों के परिमाप और क्षेत्रफल तथा 3D आकृतियों के पृष्ठीय क्षेत्रफल और आयतन को समझाता है।'),
        focus: biList(['Know formulae for circle, trapezium and polygon areas.', 'Calculate surface area and volume of cube, cuboid and cylinder.', 'Use unit conversion before computation.']),
        example: { problem: bi('Find the area of a trapezium with parallel sides 8 cm, 12 cm and height 5 cm.', '8 cm, 12 cm समांतर भुजाओं और 5 cm ऊँचाई वाले समलंब चतुर्भुज का क्षेत्रफल ज्ञात कीजिए।'), solution: bi('Area = ½(8+12)×5 = 50 cm².', 'क्षेत्रफल = ½(8+12)×5 = 50 cm²।') },
        revision: bi('Always check which formula to use and confirm units are consistent.', 'सदैव जाँचें कि कौन सा सूत्र उपयोग करना है और इकाइयाँ समान हैं।'),
      };
    case 'Exponents and Powers':
      return {
        summary: bi('Covers laws of exponents for integers and scientific notation.', 'पूर्णांकों के लिए घातांक नियम और वैज्ञानिक संकेतन को समझाता है।'),
        focus: biList(['Apply a^m × a^n = a^(m+n) and related laws.', 'Simplify expressions with negative exponents.', 'Write large numbers in scientific notation.']),
        example: { problem: bi('Simplify 2^3 × 2^4.', '2^3 × 2^4 को सरल कीजिए।'), solution: bi('2^7 = 128.', '2^7 = 128।') },
        revision: bi('Any non-zero number raised to the power 0 equals 1.', 'कोई भी अशून्य संख्या का घातांक 0 होने पर वह 1 के बराबर होती है।'),
      };
    case 'Direct and Inverse Proportions':
      return {
        summary: bi('Explains when quantities increase/decrease together (direct) or in opposite ways (inverse).', 'यह बताता है कि मात्राएँ साथ बढ़ती/घटती हैं (प्रत्यक्ष) या विपरीत दिशा में (व्युत्क्रम)।'),
        focus: biList(['Identify direct and inverse proportion situations.', 'Use y = kx for direct and xy = k for inverse proportion.', 'Solve word problems using proportion tables.']),
        example: { problem: bi('If 6 workers complete a job in 10 days, how long will 4 workers take?', 'यदि 6 कामगार 10 दिनों में काम पूरा करते हैं, तो 4 कामगार कितने दिन लेंगे?'), solution: bi('More workers, fewer days: 6×10 = 4×d, so d = 15 days.', 'अधिक कामगार, कम दिन: 6×10 = 4×d, इसलिए d = 15 दिन।') },
        revision: bi('In inverse proportion, as one quantity increases the other decreases proportionally.', 'व्युत्क्रमानुपात में, जैसे-जैसे एक मात्रा बढ़ती है, दूसरी आनुपातिक रूप से घटती है।'),
      };
    case 'Factorisation':
      return {
        summary: bi('Covers factorisation of algebraic expressions by common factor, grouping and identities.', 'उभयनिष्ठ गुणनखंड, समूहन और सर्वसमिकाओं द्वारा बीजीय व्यंजकों का गुणनखंडन समझाता है।'),
        focus: biList(['Find the highest common factor of terms.', 'Use grouping to factorise four-term expressions.', 'Apply identities like a^2 - b^2 = (a+b)(a-b).']),
        example: { problem: bi('Factorise x^2 - 9.', 'x^2 - 9 को गुणनखंडित कीजिए।'), solution: bi('(x+3)(x-3).', '(x+3)(x-3)।') },
        revision: bi('Factorisation is the reverse of multiplication; always check by expanding.', 'गुणनखंडन गुणन का उलटा है; विस्तार करके हमेशा जाँचें।'),
      };
    case 'Introduction to Graphs':
      return {
        summary: bi('Introduces bar graphs, line graphs, pie charts and coordinate plotting.', 'दंड आरेख, रेखा ग्राफ, पाई चार्ट और निर्देशांक आलेखन को समझाता है।'),
        focus: biList(['Read and draw different types of graphs.', 'Interpret trends from line graphs.', 'Plot points on a coordinate plane.']),
        example: { problem: bi('Draw a bar graph for the data: Mon-5, Tue-8, Wed-6.', 'डेटा के लिए दंड आरेख बनाइए: सोम-5, मंगल-8, बुध-6।'), solution: bi('Draw bars of heights 5, 8 and 6 on the y-axis against the days.', 'y-अक्ष पर दिनों के सामने 5, 8 और 6 ऊँचाई के दंड बनाएँ।') },
        revision: bi('Choose the right graph type based on the nature of data and the message to convey.', 'डेटा की प्रकृति और संदेश के आधार पर सही ग्राफ प्रकार चुनें।'),
      };
    // ── Class 7 specific titles ────────────────────────────────────────────
    case 'Integers':
      return {
        summary: bi('Covers positive and negative integers, their operations and properties on a number line.', 'धनात्मक और ऋणात्मक पूर्णांक, उनकी संक्रियाएँ और संख्या रेखा पर गुणधर्म समझाता है।'),
        focus: biList(['Add, subtract, multiply and divide integers.', 'Use a number line for visualisation.', 'Apply properties: commutative, associative and distributive.']),
        example: { problem: bi('Find (-8) × (-5).', '(-8) × (-5) ज्ञात कीजिए।'), solution: bi('Product of two negatives is positive: 40.', 'दो ऋणात्मकों का गुणनफल धनात्मक: 40।') },
        revision: bi('Product/quotient of two integers with the same sign is positive; opposite signs give negative.', 'समान चिह्न वाले दो पूर्णांकों का गुणनफल/भागफल धनात्मक; विपरीत चिह्न देते हैं ऋणात्मक।'),
      };
    case 'Fractions and Decimals':
      return {
        summary: bi('Extends operations to fractions and decimals including multiplication and division.', 'भिन्नों और दशमलवों पर गुणा और भाग सहित संक्रियाओं को विस्तृत करता है।'),
        focus: biList(['Multiply fractions: (a/b) × (c/d) = ac/bd.', 'Divide fractions by multiplying by the reciprocal.', 'Convert between fractions and decimals.']),
        example: { problem: bi('Find 2/3 × 3/4.', '2/3 × 3/4 ज्ञात कीजिए।'), solution: bi('6/12 = 1/2.', '6/12 = 1/2।') },
        revision: bi('To divide by a fraction, multiply by its reciprocal.', 'भिन्न से भाग देने के लिए उसके व्युत्क्रम से गुणा करें।'),
      };
    case 'Simple Equations':
      return {
        summary: bi('Introduces forming and solving simple equations from real-world situations.', 'वास्तविक जीवन की स्थितियों से सरल समीकरण बनाना और हल करना सिखाता है।'),
        focus: biList(['Identify the variable and write the equation.', 'Use inverse operations to solve.', 'Verify the solution by substitution.']),
        example: { problem: bi('A number added to 7 gives 15. Find it.', 'किसी संख्या में 7 जोड़ने पर 15 मिलता है। संख्या ज्ञात कीजिए।'), solution: bi('x + 7 = 15, so x = 8.', 'x + 7 = 15, इसलिए x = 8।') },
        revision: bi('Whatever operation you do to one side of an equation, do the same to the other.', 'समीकरण के एक पक्ष पर जो संक्रिया करें, वही दूसरे पर भी करें।'),
      };
    case 'The Triangle and its Properties':
      return {
        summary: bi('Covers median, altitude, angle sum property and the Pythagoras theorem for right triangles.', 'माध्यिका, ऊँचाई, कोण-योग गुणधर्म और समकोण त्रिभुज के लिए पायथागोरस प्रमेय को समझाता है।'),
        focus: biList(['Sum of angles of a triangle = 180°.', 'Exterior angle = sum of the two non-adjacent interior angles.', 'Apply Pythagoras theorem only in right triangles.']),
        example: { problem: bi('Two angles of a triangle are 60° and 80°. Find the third.', 'एक त्रिभुज के दो कोण 60° और 80° हैं। तीसरा कोण ज्ञात कीजिए।'), solution: bi('180 - 60 - 80 = 40°.', '180 - 60 - 80 = 40°।') },
        revision: bi('The Pythagoras theorem applies only to right-angled triangles.', 'पायथागोरस प्रमेय केवल समकोण त्रिभुज पर लागू होता है।'),
      };
    case 'Perimeter and Area':
      return {
        summary: bi('Covers perimeter and area of triangles, rectangles, squares, parallelograms and circles.', 'त्रिभुज, आयत, वर्ग, समांतर चतुर्भुज और वृत्त का परिमाप और क्षेत्रफल समझाता है।'),
        focus: biList(['Use the correct formula for each shape.', 'Convert units before computing area.', 'Understand the relationship between perimeter and area.']),
        example: { problem: bi('Find the area of a parallelogram with base 8 cm and height 5 cm.', '8 cm आधार और 5 cm ऊँचाई वाले समांतर चतुर्भुज का क्षेत्रफल ज्ञात कीजिए।'), solution: bi('Area = 8 × 5 = 40 cm².', 'क्षेत्रफल = 8 × 5 = 40 cm²।') },
        revision: bi('Area of a triangle = ½ × base × height.', 'त्रिभुज का क्षेत्रफल = ½ × आधार × ऊँचाई।'),
      };
    case 'Algebraic Expressions':
      return {
        summary: bi('Introduces terms, coefficients, like and unlike terms and addition/subtraction of expressions.', 'पद, गुणांक, सजातीय और असजातीय पद तथा व्यंजकों का जोड़/घटाना समझाता है।'),
        focus: biList(['Identify terms, factors and coefficients.', 'Add and subtract like terms.', 'Evaluate expressions by substituting values.']),
        example: { problem: bi('Add 3x + 2y and x - 5y.', '3x + 2y और x - 5y को जोड़िए।'), solution: bi('4x - 3y.', '4x - 3y।') },
        revision: bi('Only like terms can be added or subtracted.', 'केवल सजातीय पदों को जोड़ा या घटाया जा सकता है।'),
      };
    case 'Symmetry':
      return {
        summary: bi('Covers line symmetry and rotational symmetry in shapes and everyday objects.', 'आकृतियों और दैनिक जीवन की वस्तुओं में रेखा-सममिति और घूर्णन सममिति को समझाता है।'),
        focus: biList(['Identify lines of symmetry in regular polygons.', 'Find the order of rotational symmetry.', 'Relate symmetry to reflection and rotation.']),
        example: { problem: bi('How many lines of symmetry does a square have?', 'एक वर्ग की कितनी सममिति रेखाएँ होती हैं?'), solution: bi('4 lines of symmetry.', '4 सममिति रेखाएँ।') },
        revision: bi('A figure has rotational symmetry if it maps onto itself after a rotation of less than 360°.', 'एक आकृति में घूर्णन सममिति है यदि 360° से कम घुमाने पर वह स्वयं पर आ जाती है।'),
      };
    case 'Visualising Solid Shapes':
      return {
        summary: bi('Introduces 3D shapes, their nets, views and Euler\'s formula for polyhedra.', '3D आकृतियाँ, उनके जाल, दृश्य और बहुफलकों के लिए ऑयलर के सूत्र को समझाता है।'),
        focus: biList(['Identify faces, edges and vertices.', 'Draw and match nets for 3D shapes.', "Apply Euler's formula: F + V - E = 2."]),
        example: { problem: bi('How many faces, edges and vertices does a cube have?', 'घन के कितने फलक, कोर और शीर्ष होते हैं?'), solution: bi('6 faces, 12 edges, 8 vertices. F + V - E = 6+8-12 = 2.', '6 फलक, 12 कोर, 8 शीर्ष। F + V - E = 6+8-12 = 2।') },
        revision: bi("Euler's formula F + V - E = 2 holds for all convex polyhedra.", 'ऑयलर का सूत्र F + V - E = 2 सभी उत्तल बहुफलकों के लिए सत्य है।'),
      };
    // ── Class 6 specific titles ────────────────────────────────────────────
    case 'Knowing Our Numbers':
      return {
        summary: bi('Introduces place value, large numbers, estimation and Roman numerals.', 'स्थानीय मान, बड़ी संख्याएँ, आकलन और रोमन अंकों को समझाता है।'),
        focus: biList(['Read and write numbers in Indian and International systems.', 'Use place value for comparison and ordering.', 'Estimate sums, differences and products.']),
        example: { problem: bi('Write 35,67,108 in words (Indian system).', '35,67,108 को शब्दों में लिखिए (भारतीय पद्धति)।'), solution: bi('Thirty five lakh sixty seven thousand one hundred and eight.', 'पैंतीस लाख सड़सठ हजार एक सौ आठ।') },
        revision: bi('In the Indian system the periods are ones, thousands, lakhs and crores.', 'भारतीय पद्धति में अवधियाँ हैं: इकाई, हजार, लाख और करोड़।'),
      };
    case 'Whole Numbers':
      return {
        summary: bi('Covers properties of whole numbers: closure, commutativity, associativity and distributivity.', 'पूर्ण संख्याओं के संवृत्तता, क्रमविनिमय, साहचर्य और वितरण गुणधर्म समझाता है।'),
        focus: biList(['Identify natural and whole numbers on a number line.', 'Apply properties to simplify computations.', 'Use patterns in whole number operations.']),
        example: { problem: bi('Check if 15 × (6 + 4) = 15 × 6 + 15 × 4.', 'जाँचें: 15 × (6 + 4) = 15 × 6 + 15 × 4।'), solution: bi('Both sides equal 150. Distributive property holds.', 'दोनों पक्ष 150 हैं। वितरण नियम सत्य है।') },
        revision: bi('Whole numbers include zero; natural numbers do not.', 'पूर्ण संख्याओं में शून्य शामिल है; प्राकृत संख्याओं में नहीं।'),
      };
    case 'Playing with Numbers':
      return {
        summary: bi('Covers factors, multiples, prime numbers, HCF and LCM.', 'गुणनखंड, गुणज, अभाज्य संख्याएँ, HCF और LCM को समझाता है।'),
        focus: biList(['Find factors and multiples systematically.', 'Use the sieve of Eratosthenes for primes.', 'Find HCF and LCM by prime factorisation.']),
        example: { problem: bi('Find the HCF of 12 and 18.', '12 और 18 का HCF ज्ञात कीजिए।'), solution: bi('Factors of 12: 1,2,3,4,6,12; of 18: 1,2,3,6,9,18. HCF = 6.', '12 के गुणनखंड: 1,2,3,4,6,12; 18 के: 1,2,3,6,9,18। HCF = 6।') },
        revision: bi('HCF is the largest common factor; LCM is the smallest common multiple.', 'HCF सबसे बड़ा उभयनिष्ठ गुणनखंड है; LCM सबसे छोटा उभयनिष्ठ गुणज है।'),
      };
    case 'Basic Geometrical Ideas':
      return {
        summary: bi('Introduces points, lines, line segments, rays, angles and polygons.', 'बिंदु, रेखा, रेखाखंड, किरण, कोण और बहुभुजों को समझाता है।'),
        focus: biList(['Distinguish point, line, ray and line segment.', 'Name angles and identify their types.', 'Identify parts of a circle.']),
        example: { problem: bi('How is a ray different from a line?', 'किरण और रेखा में क्या अंतर है?'), solution: bi('A ray has one endpoint and extends in one direction; a line extends in both directions.', 'किरण का एक अंत-बिंदु होता है और वह एक दिशा में जाती है; रेखा दोनों दिशाओं में जाती है।') },
        revision: bi('A line has no endpoints; a line segment has two.', 'रेखा के कोई अंत-बिंदु नहीं होते; रेखाखंड के दो होते हैं।'),
      };
    case 'Understanding Elementary Shapes':
      return {
        summary: bi('Covers measuring and classifying line segments, angles and 3D shapes.', 'रेखाखंडों, कोणों और 3D आकृतियों को मापना और वर्गीकृत करना समझाता है।'),
        focus: biList(['Use a ruler and compass for measurement.', 'Classify angles: acute, right, obtuse, straight and reflex.', 'Identify 3D shapes: cube, cuboid, sphere, cylinder and cone.']),
        example: { problem: bi('Classify an angle of 120°.', '120° के कोण का वर्गीकरण कीजिए।'), solution: bi('Obtuse angle (between 90° and 180°).', 'अधिक कोण (90° और 180° के बीच)।') },
        revision: bi('A right angle is exactly 90°; a straight angle is 180°.', 'समकोण ठीक 90° होता है; सरल कोण 180° होता है।'),
      };
    case 'Fractions':
      return {
        summary: bi('Introduces fractions, equivalent fractions, comparison and basic operations.', 'भिन्न, समतुल्य भिन्न, तुलना और मूल संक्रियाओं को समझाता है।'),
        focus: biList(['Find equivalent fractions by multiplying numerator and denominator.', 'Compare fractions with the same denominator.', 'Add and subtract like fractions.']),
        example: { problem: bi('Find three fractions equivalent to 2/3.', '2/3 के तीन समतुल्य भिन्न ज्ञात कीजिए।'), solution: bi('4/6, 6/9, 8/12.', '4/6, 6/9, 8/12।') },
        revision: bi('A fraction is in its simplest form when HCF of numerator and denominator is 1.', 'भिन्न सरलतम रूप में होती है जब अंश और हर का HCF 1 हो।'),
      };
    case 'Decimals':
      return {
        summary: bi('Extends place value to tenths, hundredths and basic decimal operations.', 'स्थानीय मान को दशांश, शतांश तक और मूल दशमलव संक्रियाओं को समझाता है।'),
        focus: biList(['Read and write decimals correctly.', 'Compare decimals by aligning decimal points.', 'Add and subtract decimals with regrouping.']),
        example: { problem: bi('Add 1.25 and 2.6.', '1.25 और 2.6 जोड़िए।'), solution: bi('1.25 + 2.60 = 3.85.', '1.25 + 2.60 = 3.85।') },
        revision: bi('Align decimal points when adding or subtracting decimals.', 'दशमलव जोड़ते या घटाते समय दशमलव बिंदु एक सीध में रखें।'),
      };
    case 'Algebra':
      return {
        summary: bi('Introduces variables, constants and simple expressions as a bridge from arithmetic.', 'चर, स्थिरांक और सरल व्यंजकों को अंकगणित से बीजगणित तक के सेतु के रूप में समझाता है।'),
        focus: biList(['Use a variable to represent an unknown.', 'Write and evaluate simple algebraic expressions.', 'Form and solve simple equations.']),
        example: { problem: bi('If a = 5, find 3a + 2.', 'यदि a = 5 हो, तो 3a + 2 ज्ञात कीजिए।'), solution: bi('3(5) + 2 = 17.', '3(5) + 2 = 17।') },
        revision: bi('A variable can take different values; its value changes the value of the expression.', 'चर के मान बदल सकते हैं; इसका मान व्यंजक का मान बदलता है।'),
      };
    case 'Ratio and Proportion':
      return {
        summary: bi('Covers ratio, equivalent ratios and the concept of proportion in daily-life problems.', 'अनुपात, समतुल्य अनुपात और दैनिक जीवन में समानुपात की अवधारणा को समझाता है।'),
        focus: biList(['Write and simplify ratios.', 'Identify proportion: a:b = c:d means ad = bc.', 'Solve simple proportion problems.']),
        example: { problem: bi('If 4:6 = x:9, find x.', 'यदि 4:6 = x:9 हो, तो x ज्ञात कीजिए।'), solution: bi('Cross multiply: 4×9 = 6×x, so x = 6.', 'क्रास गुणा: 4×9 = 6×x, इसलिए x = 6।') },
        revision: bi('In a proportion a:b = c:d, the product of means equals the product of extremes.', 'समानुपात a:b = c:d में मध्य पदों का गुणनफल = अंत्य पदों का गुणनफल।'),
      };
    default:
      return {
        summary: bi(`Core ideas for ${english} at ${classLabel} level.`, `${classLabel} स्तर पर ${titleHi} के मूल विचार।`),
        focus: biList(['Read the concept carefully.', 'Practice one example.', 'Revise the key rule.']),
        example: { problem: bi(`Apply ${english} in one short school question.`, `${titleHi} पर एक छोटा स्कूल प्रश्न लागू कीजिए।`), solution: bi('Use the main rule from the chapter.', 'अध्याय के मुख्य नियम का उपयोग करें।') },
        revision: bi(`Use ${english} for quick recall.`, `त्वरित पुनरावृत्ति के लिए ${titleHi} का उपयोग करें।`),
      };
  }
}

function buildScienceHintFromTitle(title, classLabel) {
  const english = resolveText(title, 'english');
  const titleHi = resolveText(title, 'hindi');
  switch (english) {
    case 'Food: Where Does It Come From?':
      return {
        summary: bi('Introduces food sources, plant parts and the idea that different organisms depend on different food chains.', 'भोजन के स्रोत, पौधों के भाग और अलग-अलग जीवों के भोजन-आधार को समझाता है।'),
        focus: biList(['Identify plant and animal sources of food.', 'Connect edible parts of plants with daily meals.', 'Recognise that food comes from agriculture, animals and natural products.']),
        example: { problem: bi('Name two plant parts we eat.', 'हम पौधों के किन दो भागों को खाते हैं?'), solution: bi('Roots and leaves, such as carrot and spinach.', 'जड़ और पत्तियाँ, जैसे गाजर और पालक।') },
        revision: bi('Food comes from plants, animals and other natural sources.', 'भोजन पौधों, जंतुओं और अन्य प्राकृतिक स्रोतों से मिलता है।'),
      };
    case 'Components of Food':
      return {
        summary: bi('Covers nutrients, balanced diet and the role of carbohydrate, protein, fat, vitamins and minerals.', 'पोषक तत्त्व, संतुलित आहार तथा कार्बोहाइड्रेट, प्रोटीन, वसा, विटामिन और खनिजों की भूमिका को समझाता है।'),
        focus: biList(['Identify major nutrients and their functions.', 'Recognise deficiency diseases at a school level.', 'Choose food combinations that make a balanced diet.']),
        example: { problem: bi('Why is a balanced diet important?', 'संतुलित आहार क्यों महत्वपूर्ण है?'), solution: bi('It provides all nutrients in the right amount for growth and health.', 'यह वृद्धि और स्वास्थ्य के लिए उचित मात्रा में सभी पोषक तत्त्व देता है।') },
        revision: bi('A balanced diet contains carbohydrates, proteins, fats, vitamins, minerals, roughage and water.', 'संतुलित आहार में कार्बोहाइड्रेट, प्रोटीन, वसा, विटामिन, खनिज, रेशा और जल होते हैं।'),
      };
    case 'Fibre to Fabric':
      return {
        summary: bi('Explains fibres, yarn making and the journey from natural fibres to finished clothes.', 'रेशों, सूत निर्माण और प्राकृतिक रेशों से तैयार कपड़ों तक की यात्रा को समझाता है।'),
        focus: biList(['Distinguish plant and animal fibres.', 'Understand spinning, weaving and knitting.', 'Know where cotton and wool come from.']),
        example: { problem: bi('Which process turns fibre into yarn?', 'रेशे को सूत में बदलने की प्रक्रिया कौन सी है?'), solution: bi('Spinning.', 'कताई।') },
        revision: bi('Clothes begin as fibres that are spun, woven or knitted.', 'कपड़े रेशों से शुरू होकर कताई, बुनाई या बुनकर बनाए जाते हैं।'),
      };
    case 'Sorting Materials into Groups':
      return {
        summary: bi('Covers properties used to classify materials such as lustre, hardness, solubility and conductivity.', 'चमक, कठोरता, घुलनशीलता और चालकता जैसे गुणों के आधार पर पदार्थों का वर्गीकरण समझाता है।'),
        focus: biList(['Group objects by observable properties.', 'Use materials suited to specific purposes.', 'Understand transparent, translucent and opaque objects.']),
        example: { problem: bi('Why is glass chosen for windows?', 'खिड़कियों के लिए काँच क्यों चुना जाता है?'), solution: bi('Because it is transparent and lets light pass through.', 'क्योंकि यह पारदर्शी है और प्रकाश को गुजरने देता है।') },
        revision: bi('We classify materials to study them and use them better.', 'पदार्थों को वर्गीकृत करने से उनका अध्ययन और उपयोग आसान होता है।'),
      };
    case 'Separation of Substances':
      return {
        summary: bi('Introduces handpicking, sieving, filtration, evaporation, sedimentation and decantation.', 'हाथ से चुनना, छलनी, निस्यंदन, वाष्पीकरण, अवसादन और निस्तारण को समझाता है।'),
        focus: biList(['Choose the right separation method for the mixture.', 'Use evaporation to recover dissolved solids.', 'Understand why we separate substances in daily life.']),
        example: { problem: bi('How do you separate tea leaves from tea?', 'चाय पत्ती को चाय से कैसे अलग करेंगे?'), solution: bi('By filtration using a strainer.', 'छलनी से निस्यंदन द्वारा।') },
        revision: bi('Different mixtures need different separation methods.', 'अलग-अलग मिश्रणों के लिए अलग-अलग पृथक्करण विधियाँ होती हैं।'),
      };
    case 'Changes Around Us':
      return {
        summary: bi('Builds observation of reversible and irreversible changes in daily life.', 'दैनिक जीवन में प्रतिवर्ती और अप्रतिवर्ती परिवर्तनों का अवलोकन कराता है।'),
        focus: biList(['Separate physical and chemical changes.', 'Notice reversible and irreversible changes.', 'Connect changes with heating, cooling and mixing.']),
        example: { problem: bi('Is melting of ice reversible?', 'बर्फ का पिघलना प्रतिवर्ती है?'), solution: bi('Yes, water can be frozen again.', 'हाँ, पानी को फिर से जमा सकते हैं।') },
        revision: bi('A change may alter shape, state or composition.', 'परिवर्तन आकार, अवस्था या संरचना बदल सकता है।'),
      };
    case 'Getting to Know Plants':
      return {
        summary: bi('Introduces plant parts, roots, stems, leaves, flowers and simple plant functions.', 'पौधों के भाग, जड़, तना, पत्ती, फूल और उनके कार्यों को समझाता है।'),
        focus: biList(['Identify root, stem and leaf functions.', 'Distinguish tap root and fibrous root.', 'Observe flower parts and their roles.']),
        example: { problem: bi('What does the stem do?', 'तना क्या करता है?'), solution: bi('It supports the plant and transports water and food.', 'यह पौधे को सहारा देता है और जल तथा भोजन का परिवहन करता है।') },
        revision: bi('Different plant parts work together for survival.', 'पौधे के विभिन्न भाग जीवित रहने के लिए मिलकर काम करते हैं।'),
      };
    case 'Body Movements':
      return {
        summary: bi('Explains bones, joints and movement in humans and other animals.', 'मनुष्यों और अन्य जंतुओं में हड्डियों, जोड़ और गति को समझाता है।'),
        focus: biList(['Name major joints and their movements.', 'Understand skeleton and muscles.', 'Compare movement in different animals.']),
        example: { problem: bi('Which joint allows the arm to bend?', 'बाँह को मोड़ने में कौन सा जोड़ मदद करता है?'), solution: bi('The elbow joint.', 'कोहनी का जोड़।') },
        revision: bi('Movement needs bones, muscles and joints.', 'गति के लिए हड्डियाँ, मांसपेशियाँ और जोड़ आवश्यक हैं।'),
      };
    case 'The Living Organisms and Their Surroundings':
      return {
        summary: bi('Covers habitats, adaptation and the relationship between living beings and their environment.', 'आवास, अनुकूलन और जीवों तथा उनके पर्यावरण के संबंध को समझाता है।'),
        focus: biList(['Recognise habitat and adaptation.', 'Compare living and non-living things.', 'See how organisms survive in different conditions.']),
        example: { problem: bi('Why do camels survive in deserts?', 'ऊँट रेगिस्तान में क्यों जीवित रहते हैं?'), solution: bi('They have adaptations like fat storage and water conservation.', 'उनमें वसा भंडारण और जल संरक्षण जैसी अनुकूलताएँ होती हैं।') },
        revision: bi('Adaptation helps organisms live in a particular habitat.', 'अनुकूलन जीवों को विशेष आवास में रहने में मदद करता है।'),
      };
    case 'Motion and Measurement of Distances':
      return {
        summary: bi('Introduces motion, standard units and measurement tools used in science.', 'गति, मानक इकाइयाँ और मापन उपकरणों को समझाता है।'),
        focus: biList(['Use correct units.', 'Measure length with suitable instruments.', 'Identify regular and irregular motion at a basic level.']),
        example: { problem: bi('Which unit is commonly used for length?', 'लंबाई के लिए सामान्यतः कौन सी इकाई उपयोग होती है?'), solution: bi('Metre.', 'मीटर।') },
        revision: bi('Measurement must use standard units for comparison.', 'तुलना के लिए मापन में मानक इकाइयों का उपयोग आवश्यक है।'),
      };
    case 'Light, Shadows and Reflections':
      return {
        summary: bi('Builds straight-line propagation of light, shadows and simple reflections.', 'प्रकाश के सीधी रेखा में चलने, छाया और सरल परावर्तन को समझाता है।'),
        focus: biList(['Observe how shadows are formed.', 'Identify opaque, translucent and transparent objects.', 'Understand reflection using mirrors and shiny surfaces.']),
        example: { problem: bi('Why does a shadow form?', 'छाया क्यों बनती है?'), solution: bi('Because light travels in a straight line and is blocked by an opaque object.', 'क्योंकि प्रकाश सीधी रेखा में चलता है और अपारदर्शी वस्तु उसे रोकती है।') },
        revision: bi('A shadow needs a light source, an object and a screen.', 'छाया के लिए प्रकाश स्रोत, वस्तु और पर्दा चाहिए।'),
      };
    case 'Electricity and Circuits':
      return {
        summary: bi('Introduces simple circuits, cell, battery, switch and the flow of current.', 'सरल परिपथ, सेल, बैटरी, स्विच और धारा के प्रवाह को समझाता है।'),
        focus: biList(['Draw a simple circuit.', 'Know open and closed circuits.', 'Identify conductors and insulators in a circuit.']),
        example: { problem: bi('What does a switch do?', 'स्विच क्या करता है?'), solution: bi('It opens or closes the circuit.', 'यह परिपथ को खोलता या बंद करता है।') },
        revision: bi('A circuit must be complete for current to flow.', 'धारा के प्रवाह के लिए परिपथ का पूरा होना आवश्यक है।'),
      };
    case 'Matter in Our Surroundings':
      return {
        summary: bi('Explains matter, particles, states and evaporation basics.', 'पदार्थ, उसके कण, अवस्थाएँ और वाष्पीकरण की मूल बातें समझाता है।'),
        focus: biList(['Define matter correctly.', 'Compare solids, liquids and gases.', 'Understand evaporation and cooling.']),
        example: { problem: bi('Why does wet clothes feel cool?', 'गीले कपड़े ठंडे क्यों लगते हैं?'), solution: bi('Evaporation absorbs heat from the body.', 'वाष्पीकरण शरीर से ऊष्मा अवशोषित करता है।') },
        revision: bi('Particles have space, motion and attraction.', 'कणों में स्थान, गति और आकर्षण होता है।'),
      };
    case 'Is Matter Around Us Pure':
      return {
        summary: bi('Covers mixtures, solutions, suspensions and separation methods.', 'मिश्रण, विलयन, निलंबन और पृथक्करण विधियों को समझाता है।'),
        focus: biList(['Classify mixtures.', 'Use filtration, evaporation and distillation.', 'Know colloids and suspensions.']),
        example: { problem: bi('How to separate sand from water?', 'रेत को पानी से कैसे अलग करेंगे?'), solution: bi('By filtration.', 'निस्यंदन द्वारा।') },
        revision: bi('Pure substances have fixed composition.', 'शुद्ध पदार्थों की संरचना निश्चित होती है।'),
      };
    case 'Atoms and Molecules':
      return {
        summary: bi('Introduces law of conservation of mass and formula writing.', 'द्रव्यमान संरक्षण नियम और सूत्र लेखन को समझाता है।'),
        focus: biList(['Write chemical formulae.', 'Use atomic and molecular mass.', 'Apply laws of combination.']),
        example: { problem: bi('Write formula of calcium chloride.', 'कैल्शियम क्लोराइड का सूत्र लिखिए।'), solution: bi('CaCl2.', 'CaCl2।') },
        revision: bi('Atoms combine in fixed ratios.', 'परमाणु निश्चित अनुपात में संयोजित होते हैं।'),
      };
    case 'Structure of the Atom':
      return {
        summary: bi('Covers subatomic particles and atomic models.', 'उपपरमाणुक कण और परमाणु मॉडल समझाता है।'),
        focus: biList(['Know electron, proton and neutron.', 'Compare Thomson, Rutherford and Bohr.', 'Write electronic configuration basics.']),
        example: { problem: bi('What is charge of an electron?', 'इलेक्ट्रॉन का आवेश क्या है?'), solution: bi('-1 unit charge.', '−1 इकाई आवेश।') },
        revision: bi('Nucleus is small but dense.', 'नाभिक छोटा लेकिन घना होता है।'),
      };
    case 'The Fundamental Unit of Life':
      return {
        summary: bi('Builds the cell, its organelles and cell theory basics.', 'कोशिका, उसके अंगकों और कोशिका सिद्धांत की नींव बनाता है।'),
        focus: biList(['Cell membrane and wall.', 'Nucleus and cytoplasm.', 'Plant and animal cell differences.']),
        example: { problem: bi('Which organelle is the control center?', 'नियंत्रण केंद्र कौन सा अंगक है?'), solution: bi('Nucleus.', 'नाभिक।') },
        revision: bi('Cell is the basic unit of life.', 'कोशिका जीवन की मौलिक इकाई है।'),
      };
    case 'Tissues':
      return {
        summary: bi('Explains plant and animal tissues and their functions.', 'पौधों और जंतुओं के ऊतकों को समझाता है।'),
        focus: biList(['Meristematic and permanent tissues.', 'Xylem and phloem.', 'Simple and complex tissues.']),
        example: { problem: bi('What does xylem transport?', 'जाइलम क्या परिवहन करता है?'), solution: bi('Water and minerals.', 'जल और खनिज।') },
        revision: bi('Tissues perform specialised work.', 'ऊतक विशेष कार्य करते हैं।'),
      };
    case 'Diversity in Living Organisms':
      return {
        summary: bi('Introduces classification and biodiversity basics.', 'वर्गीकरण और जैव विविधता की मूल बातें समझाता है।'),
        focus: biList(['Use classification hierarchy.', 'Know kingdom-level grouping.', 'Recognise basic plant and animal groups.']),
        example: { problem: bi('Why classify organisms?', 'जीवों का वर्गीकरण क्यों करते हैं?'), solution: bi('To study them systematically.', 'उन्हें व्यवस्थित रूप से अध्ययन करने के लिए।') },
        revision: bi('Classification reduces confusion and helps comparison.', 'वर्गीकरण भ्रम कम करता है और तुलना में मदद करता है।'),
      };
    case 'Motion':
      return {
        summary: bi('Covers distance, displacement, speed, velocity and graphs.', 'दूरी, विस्थापन, चाल, वेग और ग्राफ को समझाता है।'),
        focus: biList(['Distinguish distance and displacement.', 'Use speed and velocity formulae.', 'Read motion graphs.']),
        example: { problem: bi('Find speed if 100 m is covered in 20 s.', 'यदि 100 m दूरी 20 s में तय हो तो चाल ज्ञात कीजिए।'), solution: bi('5 m/s.', '5 m/s।') },
        revision: bi('Motion is described with reference to a frame.', 'गति को किसी संदर्भ के सापेक्ष समझा जाता है।'),
      };
    case 'Force and Laws of Motion':
      return {
        summary: bi('Explains inertia, force and Newton\'s laws.', 'जड़त्व, बल और न्यूटन के नियम समझाता है।'),
        focus: biList(['Inertia and momentum.', 'First, second and third law.', 'Force changes state of motion.']),
        example: { problem: bi('Why do passengers jerk forward when bus stops?', 'बस रुकने पर यात्री आगे की ओर क्यों झटका खाते हैं?'), solution: bi('Due to inertia of motion.', 'गति के जड़त्व के कारण।') },
        revision: bi('Force changes momentum.', 'बल संवेग बदलता है।'),
      };
    case 'Gravitation':
      return {
        summary: bi('Covers universal gravitation, free fall and buoyancy basics.', 'सार्वत्रिक गुरुत्वाकर्षण, मुक्त पतन और उत्प्लावन की मूल बातें समझाता है।'),
        focus: biList(['Know g and weight.', 'Understand mass vs weight.', 'Use Archimedes\' principle at school level.']),
        example: { problem: bi('What happens to weight on Moon?', 'चंद्रमा पर भार का क्या होता है?'), solution: bi('It becomes one-sixth.', 'यह एक-छठा हो जाता है।') },
        revision: bi('Mass stays same, weight changes with g.', 'द्रव्यमान समान रहता है, भार g के साथ बदलता है।'),
      };
    case 'Work and Energy':
      return {
        summary: bi('Introduces work, energy, power and conservation.', 'कार्य, ऊर्जा, शक्ति और संरक्षण को समझाता है।'),
        focus: biList(['Use W = F x s.', 'Distinguish kinetic and potential energy.', 'Remember power as rate of doing work.']),
        example: { problem: bi('Work done by 10 N force over 2 m.', '10 N बल द्वारा 2 m पर किया गया कार्य ज्ञात कीजिए।'), solution: bi('20 J.', '20 J।') },
        revision: bi('Energy is capacity to do work.', 'ऊर्जा कार्य करने की क्षमता है।'),
      };
    case 'Sound':
      return {
        summary: bi('Covers production, propagation and characteristics of sound.', 'ध्वनि का उत्पादन, प्रसार और गुण समझाता है।'),
        focus: biList(['Use amplitude, frequency, time period.', 'Understand echo and reflection.', 'Know human hearing range.']),
        example: { problem: bi('What is the frequency of sound?', 'ध्वनि की आवृत्ति क्या है?'), solution: bi('Number of oscillations per second.', 'प्रति सेकंड दोलनों की संख्या।') },
        revision: bi('Sound needs a material medium.', 'ध्वनि को संचरण के लिए माध्यम चाहिए।'),
      };
    case 'Why Do We Fall Ill':
      return {
        summary: bi('Explains health, disease, prevention and immunity basics.', 'स्वास्थ्य, रोग, रोकथाम और प्रतिरक्षा की मूल बातें समझाता है।'),
        focus: biList(['Differentiate health and disease.', 'Know infectious and non-infectious diseases.', 'Remember prevention methods.']),
        example: { problem: bi('Why is vaccination important?', 'टीकाकरण क्यों महत्वपूर्ण है?'), solution: bi('It helps build immunity before infection.', 'यह संक्रमण से पहले प्रतिरक्षा बनाने में मदद करता है।') },
        revision: bi('Prevention is better than cure.', 'रोकथाम इलाज से बेहतर है।'),
      };
    case 'Natural Resources':
      return {
        summary: bi('Introduces air, water, soil, cycles and conservation.', 'वायु, जल, मृदा, चक्र और संरक्षण को समझाता है।'),
        focus: biList(['Atmosphere and biosphere.', 'Water and nitrogen cycles.', 'Ozone and resource conservation.']),
        example: { problem: bi('Why is the ozone layer important?', 'ओज़ोन परत महत्वपूर्ण क्यों है?'), solution: bi('It protects from harmful UV rays.', 'यह हानिकारक UV किरणों से बचाती है।') },
        revision: bi('Resources must be conserved for future use.', 'संसाधनों का संरक्षण भविष्य के लिए जरूरी है।'),
      };
    case 'Improvement in Food Resources':
      return {
        summary: bi('Covers better crop production and animal husbandry methods.', 'फसल उत्पादन और पशुपालन के बेहतर तरीकों को समझाता है।'),
        focus: biList(['Crop variety improvement.', 'Manure, fertiliser and irrigation.', 'Animal rearing and production.']),
        example: { problem: bi('Why use manure?', 'खाद क्यों उपयोग करते हैं?'), solution: bi('It improves soil fertility.', 'यह मिट्टी की उर्वरता बढ़ाती है।') },
        revision: bi('Good food resources need science and care.', 'अच्छे खाद्य संसाधनों के लिए विज्ञान और देखभाल दोनों जरूरी हैं।'),
      };
    // ── Class 7 science ────────────────────────────────────────────────────
    case 'Nutrition in Plants':
      return {
        summary: bi('Explains photosynthesis, modes of nutrition and the role of chlorophyll.', 'प्रकाश-संश्लेषण, पोषण के तरीके और क्लोरोफिल की भूमिका को समझाता है।'),
        focus: biList(['Understand autotrophic and heterotrophic nutrition.', 'Know the conditions for photosynthesis.', 'Identify saprophytes and parasites.']),
        example: { problem: bi('What are the raw materials for photosynthesis?', 'प्रकाश-संश्लेषण के लिए कच्ची सामग्री क्या है?'), solution: bi('Carbon dioxide, water and sunlight.', 'कार्बन डाइऑक्साइड, जल और सूर्य का प्रकाश।') },
        revision: bi('Photosynthesis: 6CO₂ + 6H₂O + light → C₆H₁₂O₆ + 6O₂.', 'प्रकाश-संश्लेषण: 6CO₂ + 6H₂O + प्रकाश → C₆H₁₂O₆ + 6O₂।'),
      };
    case 'Nutrition in Animals':
      return {
        summary: bi('Covers the human digestive system and the process of digestion from mouth to small intestine.', 'मानव पाचन तंत्र और मुँह से छोटी आँत तक पाचन प्रक्रिया को समझाता है।'),
        focus: biList(['Know organs: mouth, oesophagus, stomach, small intestine, large intestine.', 'Understand the role of enzymes in digestion.', 'Recognise ingestion, digestion, absorption, assimilation and egestion.']),
        example: { problem: bi('Where does the digestion of starch begin?', 'स्टार्च का पाचन कहाँ से शुरू होता है?'), solution: bi('In the mouth by salivary amylase.', 'मुँह में लार-ऐमिलेस द्वारा।') },
        revision: bi('Digestion breaks large food molecules into smaller absorbable units.', 'पाचन बड़े खाद्य अणुओं को छोटी, अवशोषणीय इकाइयों में तोड़ता है।'),
      };
    case 'Heat':
      return {
        summary: bi('Covers temperature measurement, conduction, convection and radiation.', 'तापमान मापन, चालन, संवहन और विकिरण को समझाता है।'),
        focus: biList(['Distinguish heat and temperature.', 'Know good and bad conductors.', 'Describe land and sea breeze using convection.']),
        example: { problem: bi('Why do we wear woollen clothes in winter?', 'सर्दियों में ऊनी कपड़े क्यों पहनते हैं?'), solution: bi('Wool is a bad conductor and traps air to keep us warm.', 'ऊन कुचालक है और हवा को फँसाकर हमें गर्म रखती है।') },
        revision: bi('Heat flows from a hotter body to a cooler body.', 'ऊष्मा अधिक गर्म पिंड से कम गर्म पिंड की ओर बहती है।'),
      };
    case 'Physical and Chemical Changes':
      return {
        summary: bi('Distinguishes physical changes (reversible, no new substance) from chemical changes.', 'भौतिक परिवर्तन (प्रतिवर्ती, नया पदार्थ नहीं) और रासायनिक परिवर्तन में अंतर समझाता है।'),
        focus: biList(['Signs of chemical change: gas, heat, colour, precipitate.', 'Rusting is a slow chemical change; burning is fast.', 'Crystallisation is a physical change.']),
        example: { problem: bi('Is dissolving sugar in water a physical or chemical change?', 'पानी में चीनी घोलना भौतिक है या रासायनिक परिवर्तन?'), solution: bi('Physical change; sugar can be recovered by evaporation.', 'भौतिक परिवर्तन; वाष्पन से चीनी वापस पाई जा सकती है।') },
        revision: bi('Chemical changes form new substances with different properties.', 'रासायनिक परिवर्तनों से भिन्न गुणधर्मों वाले नए पदार्थ बनते हैं।'),
      };
    case 'Weather, Climate and Adaptations':
      return {
        summary: bi('Explains weather vs climate and how animals adapt to extreme climates.', 'मौसम और जलवायु का अंतर तथा जंतुओं का चरम जलवायु में अनुकूलन समझाता है।'),
        focus: biList(['Distinguish weather (short-term) from climate (long-term).', 'Know adaptations of polar and tropical animals.', 'Identify major climate zones.']),
        example: { problem: bi('Why do polar bears have thick white fur?', 'ध्रुवीय भालू का मोटा सफेद फर क्यों होता है?'), solution: bi('White fur provides camouflage; thick fur insulates against cold.', 'सफेद फर छलावरण देता है; मोटा फर ठंड से बचाता है।') },
        revision: bi('Adaptations are special features that help organisms survive in their habitat.', 'अनुकूलन विशेष लक्षण हैं जो जीवों को अपने आवास में जीवित रहने में मदद करते हैं।'),
      };
    case 'Winds, Storms and Cyclones':
      return {
        summary: bi('Explains wind formation, pressure differences, thunderstorms and cyclones.', 'पवन निर्माण, दाब अंतर, गरज-तूफान और चक्रवात को समझाता है।'),
        focus: biList(['Warm air rises creating low pressure; cool air flows in.', 'Speed of wind increases as pressure difference increases.', 'Know safety measures during storms.']),
        example: { problem: bi('Why do cyclones form near the sea?', 'समुद्र के पास चक्रवात क्यों बनते हैं?'), solution: bi('Warm moist air over the sea rises rapidly, creating a low-pressure centre.', 'समुद्र पर गर्म नम हवा तेज़ी से उठती है, जिससे कम दाब का केंद्र बनता है।') },
        revision: bi('High winds and low air pressure together create destructive storms.', 'तेज़ पवन और कम वायुदाब मिलकर विनाशकारी तूफान बनाते हैं।'),
      };
    case 'Soil':
      return {
        summary: bi('Covers soil formation, soil types, soil profile and soil erosion.', 'मृदा निर्माण, मृदा प्रकार, मृदा प्रोफाइल और मृदा अपरदन को समझाता है।'),
        focus: biList(['Understand how rock weathers to form soil.', 'Know the layers: humus-rich topsoil, subsoil and bedrock.', 'Recognise causes and prevention of soil erosion.']),
        example: { problem: bi('What is humus and why is it important?', 'ह्यूमस क्या है और यह महत्वपूर्ण क्यों है?'), solution: bi('Humus is decayed organic matter; it improves soil fertility and water retention.', 'ह्यूमस सड़ी जैविक सामग्री है; यह मिट्टी की उर्वरता और जल-धारण में सुधार करता है।') },
        revision: bi('Soil is a mixture of minerals, organic matter, water and air.', 'मृदा खनिजों, कार्बनिक पदार्थ, जल और वायु का मिश्रण है।'),
      };
    case 'Respiration in Organisms':
      return {
        summary: bi('Covers aerobic and anaerobic respiration, breathing organs and gas exchange.', 'वायवीय और अवायवीय श्वसन, श्वसन अंगों और गैस विनिमय को समझाता है।'),
        focus: biList(['Distinguish breathing from cellular respiration.', 'Know aerobic: glucose + O₂ → CO₂ + H₂O + energy.', 'Identify anaerobic pathways in yeast and muscles.']),
        example: { problem: bi('Why do we breathe faster during exercise?', 'व्यायाम के दौरान हम तेज़ी से साँस क्यों लेते हैं?'), solution: bi('Muscles need more oxygen and energy; CO₂ builds up faster.', 'मांसपेशियों को अधिक ऑक्सीजन और ऊर्जा चाहिए; CO₂ तेज़ी से जमा होती है।') },
        revision: bi('Respiration releases energy from food for all life activities.', 'श्वसन भोजन से ऊर्जा मुक्त करता है जो सभी जीवन क्रियाओं के लिए उपयोगी है।'),
      };
    case 'Transportation in Animals and Plants':
      return {
        summary: bi('Covers circulation in animals and water/mineral transport in plants via xylem and phloem.', 'जंतुओं में परिसंचरण और पौधों में जाइलम व फ्लोएम द्वारा जल/खनिज परिवहन को समझाता है।'),
        focus: biList(['Trace the path of blood through the heart.', 'Know the role of xylem (water up) and phloem (food down).', 'Understand transpiration and its effect.']),
        example: { problem: bi('Which blood vessels carry oxygenated blood away from the heart?', 'कौन सी रक्त वाहिकाएँ हृदय से ऑक्सीजनयुक्त रक्त ले जाती हैं?'), solution: bi('Arteries.', 'धमनियाँ।') },
        revision: bi('Xylem transports water upward; phloem transports food in both directions.', 'जाइलम जल को ऊपर ले जाता है; फ्लोएम भोजन को दोनों दिशाओं में।'),
      };
    case 'Reproduction in Plants':
      return {
        summary: bi('Covers vegetative propagation, budding, fragmentation, spore formation and sexual reproduction in plants.', 'कायिक प्रवर्धन, मुकुलन, विखंडन, बीजाणु निर्माण और पौधों में लैंगिक जनन समझाता है।'),
        focus: biList(['Identify methods of asexual reproduction in plants.', 'Know the parts of a flower and their roles.', 'Understand pollination and fertilisation.']),
        example: { problem: bi('Name a plant that reproduces by leaves.', 'उस पौधे का नाम बताइए जो पत्तियों द्वारा प्रजनन करता है।'), solution: bi('Bryophyllum.', 'ब्रायोफिलम।') },
        revision: bi('In sexual reproduction, male and female gametes fuse to form a zygote.', 'लैंगिक जनन में नर और मादा युग्मक मिलकर युग्मनज बनाते हैं।'),
      };
    case 'Motion and Time':
      return {
        summary: bi('Introduces speed, time-distance graphs and units of time measurement.', 'चाल, समय-दूरी आलेख और समय मापन की इकाइयों को समझाता है।'),
        focus: biList(['Use speed = distance/time.', 'Read and draw distance-time graphs.', 'Convert units: km/h to m/s.']),
        example: { problem: bi('A car travels 60 km in 2 hours. Find its speed.', 'एक कार 2 घंटे में 60 km यात्रा करती है। चाल ज्ञात कीजिए।'), solution: bi('Speed = 60/2 = 30 km/h.', 'चाल = 60/2 = 30 km/h।') },
        revision: bi('A straight distance-time graph means uniform speed.', 'सीधा दूरी-समय आलेख एकसमान चाल का संकेत है।'),
      };
    // ── Class 8 science ────────────────────────────────────────────────────
    case 'Crop Production and Management':
      return {
        summary: bi('Covers agricultural practices: soil preparation, sowing, manuring, irrigation, weeding and harvesting.', 'कृषि प्रक्रियाएँ: मिट्टी की तैयारी, बुवाई, खाद, सिंचाई, निराई और कटाई को समझाता है।'),
        focus: biList(['Know kharif and rabi crops.', 'Understand traditional vs modern irrigation methods.', 'Distinguish manure from fertiliser.']),
        example: { problem: bi('Name one kharif and one rabi crop.', 'एक खरीफ और एक रबी फसल का नाम बताइए।'), solution: bi('Kharif: rice; Rabi: wheat.', 'खरीफ: चावल; रबी: गेहूँ।') },
        revision: bi('Good agricultural practices increase yield while maintaining soil health.', 'अच्छी कृषि पद्धतियाँ मिट्टी की सेहत बनाए रखते हुए उपज बढ़ाती हैं।'),
      };
    case 'Microorganisms: Friend and Foe':
      return {
        summary: bi('Covers bacteria, fungi, algae, protozoa: their uses and harmful effects.', 'जीवाणु, कवक, शैवाल, प्रोटोजोआ: उनके उपयोग और हानिकारक प्रभाव समझाता है।'),
        focus: biList(['Name major types of microorganisms.', 'Know useful roles: yoghurt, antibiotics, nitrogen fixation.', 'Know harmful effects: diseases, food spoilage.']),
        example: { problem: bi('How does Rhizobium help plants?', 'राइजोबियम पौधों की मदद कैसे करता है?'), solution: bi('It fixes atmospheric nitrogen into the soil.', 'यह वायुमंडलीय नाइट्रोजन को मिट्टी में स्थिर करता है।') },
        revision: bi('Microorganisms are too small to see with the naked eye but have enormous impact.', 'सूक्ष्मजीव नंगी आँखों से नहीं दिखते लेकिन इनका प्रभाव अत्यंत व्यापक है।'),
      };
    case 'Synthetic Fibres and Plastics':
      return {
        summary: bi('Covers synthetic fibres (nylon, polyester, acrylic) and types of plastics and their properties.', 'कृत्रिम तंतु (नायलॉन, पॉलिएस्टर, एक्रिलिक) और प्लास्टिक के प्रकार व गुणधर्म समझाता है।'),
        focus: biList(['Distinguish synthetic from natural fibres.', 'Know properties: durability, moisture, heat resistance.', 'Understand why plastics are an environmental hazard.']),
        example: { problem: bi('Why is nylon stronger than cotton?', 'नायलॉन कपास से मज़बूत क्यों है?'), solution: bi('Nylon is a polymer with long interlocked chains giving it greater tensile strength.', 'नायलॉन एक बहुलक है जिसकी लंबी जुड़ी शृंखलाएँ इसे अधिक तन्य शक्ति देती हैं।') },
        revision: bi('Plastics are non-biodegradable and must be disposed of responsibly.', 'प्लास्टिक जैव-अनिम्नीकरणीय है और इसे जिम्मेदारी से निपटाना चाहिए।'),
      };
    case 'Materials: Metals and Non-Metals':
      return {
        summary: bi('Compares physical and chemical properties of metals and non-metals and their uses.', 'धातुओं और अधातुओं के भौतिक और रासायनिक गुणधर्मों की तुलना और उनके उपयोग समझाता है।'),
        focus: biList(['Know physical properties: lustre, hardness, malleability, conductivity.', 'Understand reactivity: metals react with oxygen, water and acids.', 'Recall common uses of metals and non-metals.']),
        example: { problem: bi('Which non-metal is a good conductor of electricity?', 'कौन सी अधातु विद्युत की अच्छी चालक है?'), solution: bi('Graphite (a form of carbon).', 'ग्रेफाइट (कार्बन का एक रूप)।') },
        revision: bi('Most metals are malleable and ductile; most non-metals are brittle.', 'अधिकांश धातुएँ पर्तनीय और तन्य होती हैं; अधिकांश अधातुएँ भंगुर होती हैं।'),
      };
    case 'Coal and Petroleum':
      return {
        summary: bi('Explains the formation of fossil fuels, their refining and the need for conservation.', 'जीवाश्म ईंधन का निर्माण, उनका परिष्करण और संरक्षण की आवश्यकता समझाता है।'),
        focus: biList(['Know that coal and petroleum are fossil fuels formed over millions of years.', 'Understand fractional distillation of petroleum.', 'Recognise consequences of overuse: pollution and depletion.']),
        example: { problem: bi('Why are coal and petroleum called exhaustible resources?', 'कोयला और पेट्रोलियम को अक्षय संसाधन क्यों नहीं कहते?'), solution: bi('They take millions of years to form and are being used up faster than they are renewed.', 'इनके बनने में लाखों साल लगते हैं और ये नवीनीकरण से तेज़ गति से खर्च हो रहे हैं।') },
        revision: bi('Fossil fuels are non-renewable; alternatives like solar and wind must be developed.', 'जीवाश्म ईंधन अनवीकरणीय हैं; सौर और पवन जैसे विकल्प विकसित किए जाने चाहिए।'),
      };
    case 'Combustion and Flame':
      return {
        summary: bi('Explains combustion, ignition temperature, types of flame and the fire triangle.', 'दहन, ज्वलन तापमान, ज्वाला के प्रकार और अग्नि त्रिकोण को समझाता है।'),
        focus: biList(['Combustion requires fuel, oxygen and heat (fire triangle).', 'Distinguish luminous and non-luminous flames.', 'Know calorific value and the role of flame in cooking.']),
        example: { problem: bi('Why do we use sand to put out oil fires?', 'तेल की आग बुझाने के लिए रेत का उपयोग क्यों करते हैं?'), solution: bi('Sand cuts off oxygen supply, removing one side of the fire triangle.', 'रेत ऑक्सीजन की आपूर्ति काट देती है, जिससे अग्नि त्रिकोण का एक पक्ष हट जाता है।') },
        revision: bi('The ignition temperature is the minimum temperature at which a substance catches fire.', 'ज्वलन तापमान वह न्यूनतम तापमान है जिस पर कोई पदार्थ आग पकड़ता है।'),
      };
    case 'Conservation of Plants and Animals':
      return {
        summary: bi('Covers deforestation, biodiversity, wildlife sanctuaries, national parks and the Red Data Book.', 'वनों की कटाई, जैव विविधता, वन्यजीव अभ्यारण्य, राष्ट्रीय उद्यान और रेड डेटा बुक को समझाता है।'),
        focus: biList(['Know the difference between a wildlife sanctuary and a national park.', 'Understand causes and effects of deforestation.', 'Recall endemic species and their importance.']),
        example: { problem: bi('What is a biosphere reserve?', 'जीवमंडल आरक्षित क्षेत्र क्या है?'), solution: bi('A large protected area that conserves biodiversity and allows research.', 'एक बड़ा संरक्षित क्षेत्र जो जैव विविधता को संरक्षित करता है और अनुसंधान की अनुमति देता है।') },
        revision: bi('Conservation protects biodiversity for future generations.', 'संरक्षण भावी पीढ़ियों के लिए जैव विविधता की रक्षा करता है।'),
      };
    case 'Cell - Structure and Functions':
      return {
        summary: bi('Covers cell discovery, cell types (prokaryote/eukaryote), organelles and their functions.', 'कोशिका की खोज, प्रकार (प्रोकैरियोट/यूकैरियोट), अंगकों और उनके कार्य समझाता है।'),
        focus: biList(['Distinguish plant and animal cells.', 'Know functions of nucleus, mitochondria, chloroplast, vacuole and cell membrane.', 'Understand prokaryotic vs eukaryotic structure.']),
        example: { problem: bi('What is the function of the cell membrane?', 'कोशिका झिल्ली का क्या कार्य है?'), solution: bi('It controls the entry and exit of substances into and out of the cell.', 'यह कोशिका में पदार्थों के प्रवेश और निकास को नियंत्रित करती है।') },
        revision: bi('Mitochondria is the powerhouse of the cell; chloroplast is the site of photosynthesis in plants.', 'माइटोकॉन्ड्रिया कोशिका का बिजलीघर है; क्लोरोप्लास्ट पौधों में प्रकाश-संश्लेषण का स्थल है।'),
      };
    case 'Reproduction in Animals':
      return {
        summary: bi('Covers sexual and asexual reproduction, internal/external fertilisation and development in animals.', 'लैंगिक और अलैंगिक जनन, आंतरिक/बाहरी निषेचन और जंतुओं में विकास समझाता है।'),
        focus: biList(['Distinguish oviparous and viviparous animals.', 'Know internal and external fertilisation examples.', 'Understand cloning and its basics.']),
        example: { problem: bi('What is the difference between oviparous and viviparous animals?', 'अंडप्रसू और जरायुज जंतुओं में क्या अंतर है?'), solution: bi('Oviparous animals lay eggs; viviparous give birth to live young.', 'अंडप्रसू जंतु अंडे देते हैं; जरायुज जंतु जीवित शिशुओं को जन्म देते हैं।') },
        revision: bi('Sexual reproduction involves fusion of gametes; asexual does not.', 'लैंगिक जनन में युग्मकों का संलयन होता है; अलैंगिक में नहीं।'),
      };
    case 'Reaching the Age of Adolescence':
      return {
        summary: bi('Covers puberty changes, secondary sexual characteristics, hormones and reproductive health.', 'यौवनारंभ के परिवर्तन, द्वितीयक लैंगिक लक्षण, हार्मोन और प्रजनन स्वास्थ्य को समझाता है।'),
        focus: biList(['Know physical changes during puberty for boys and girls.', 'Understand the role of hormones: testosterone and oestrogen.', 'Discuss balanced diet and hygiene in adolescence.']),
        example: { problem: bi('Which hormone controls male puberty?', 'पुरुष यौवनारंभ को कौन सा हार्मोन नियंत्रित करता है?'), solution: bi('Testosterone produced by testes.', 'वृषण द्वारा निर्मित टेस्टोस्टेरोन।') },
        revision: bi('Adolescence is a period of rapid physical and emotional change driven by hormones.', 'किशोरावस्था हार्मोनों द्वारा संचालित तीव्र शारीरिक और भावनात्मक परिवर्तन का काल है।'),
      };
    case 'Force and Pressure':
      return {
        summary: bi('Explains force, pressure, atmospheric pressure and pressure in liquids.', 'बल, दाब, वायुमंडलीय दाब और द्रवों में दाब को समझाता है।'),
        focus: biList(['Pressure = Force/Area.', 'Understand applications of pressure: sharp knife, snowshoes.', 'Know atmospheric pressure and its effects.']),
        example: { problem: bi('Why does a sharp knife cut better than a blunt one?', 'धारदार चाकू कुंद चाकू से बेहतर क्यों काटता है?'), solution: bi('Smaller area gives greater pressure for the same force.', 'समान बल के लिए कम क्षेत्रफल अधिक दाब देता है।') },
        revision: bi('Pressure is force per unit area; unit is Pascal (Pa).', 'दाब = बल / क्षेत्रफल; इकाई पास्कल (Pa) है।'),
      };
    case 'Friction':
      return {
        summary: bi('Explains friction, its types, factors affecting it and the advantages and disadvantages of friction.', 'घर्षण, उसके प्रकार, उसे प्रभावित करने वाले कारक और घर्षण के लाभ-हानि को समझाता है।'),
        focus: biList(['Know static, kinetic and rolling friction.', 'Understand that friction depends on surface roughness and normal force.', 'List uses and disadvantages of friction in daily life.']),
        example: { problem: bi('Why is it easier to roll a box than to slide it?', 'बक्से को खिसकाने से बेहतर है उसे लुढ़काना क्यों?'), solution: bi('Rolling friction is much less than sliding friction.', 'लुढ़कन घर्षण सर्पण घर्षण से बहुत कम होता है।') },
        revision: bi('Friction is a necessary evil: it enables walking but wastes energy in machines.', 'घर्षण एक आवश्यक बुराई है: यह चलने में मदद करता है लेकिन मशीनों में ऊर्जा बर्बाद करता है।'),
      };
    default:
      return {
        summary: bi(`Core ideas for ${english} at ${classLabel} level.`, `${classLabel} स्तर पर ${titleHi} के मूल विचार।`),
        focus: biList(['Read the concept carefully.', 'Practice one example.', 'Revise the key rule.']),
        example: { problem: bi(`Apply ${english} in one short school question.`, `${titleHi} पर एक छोटा स्कूल प्रश्न लागू कीजिए।`), solution: bi('Use the main idea from the chapter.', 'अध्याय के मुख्य विचार का उपयोग करें।') },
        revision: bi(`Use ${english} for quick recall.`, `त्वरित पुनरावृत्ति के लिए ${titleHi} का उपयोग करें।`),
      };
  }
}

function getHintForChapter({ programKey, subjectKey, title, chapterId, classLabel }) {
  if (subjectKey === 'mathematics') {
    return MATH_HINTS[chapterId] || buildMathHintFromTitle(title, classLabel);
  }

  if (programKey === 'class10-science') {
    return SCIENCE_HINTS[chapterId] || buildScienceHintFromTitle(title, classLabel);
  }

  if (subjectKey === 'science') {
    return buildScienceHintFromTitle(title, classLabel);
  }

  return buildScienceHintFromTitle(title, classLabel);
}

function buildSectionList({ subjectKey, title, hint, classLabel }) {
  const en = typeof title === 'object' ? (title.english || '') : (title || '');
  const hi = typeof title === 'object' ? (title.hindi || en) : (title || '');
  const subjectLabel = subjectKey === 'science' ? 'Science' : 'Mathematics';
  const introText = bi(
    `${en} is presented here as ${classLabel} ${subjectLabel.toLowerCase()} notes with the same exam-first style used across our chapterwise library.`,
    `${hi} को यहाँ ${classLabel} ${subjectKey === 'science' ? 'विज्ञान' : 'गणित'} नोट्स के रूप में उसी परीक्षा-प्रथम शैली में प्रस्तुत किया गया है जो हमारी chapterwise library में उपयोग होती है।`
  );

  const questions = [
    bi(`What is the central idea of ${en}?`, `${hi} का मुख्य विचार क्या है?`),
    bi(`State one key exam point from ${en}.`, `${hi} से एक मुख्य परीक्षा बिंदु लिखिए।`),
    bi(`Give one real-life use of ${en}.`, `${hi} का एक दैनिक जीवन उपयोग बताइए।`),
  ];

  const answerLine = bi(
    `Use the formula, rule or concept from ${en} exactly as shown in the worked example.`,
    `${hi} के सूत्र, नियम या अवधारणा का उपयोग ठीक वैसे ही करें जैसे solved example में दिखाया गया है।`
  );

  const conceptMapRows = [
    [bi('Main idea', 'मुख्य विचार'), hint.summary],
    [bi('Key point', 'मुख्य बिंदु'), hint.focus[0]],
    [bi('Application', 'अनुप्रयोग'), hint.focus[hint.focus.length - 1]],
  ];

  const testQuestions = [
    bi(`Define the main idea of ${en} in one line.`, `${hi} के मुख्य विचार को एक पंक्ति में परिभाषित कीजिए।`),
    bi(`Write one formula/rule from ${en}.`, `${hi} का एक सूत्र/नियम लिखिए।`),
    bi(`Solve a small application from ${en}.`, `${hi} का एक छोटा अनुप्रयोग हल कीजिए।`),
    bi(`Why is ${en} useful in school exams?`, `${hi} स्कूल परीक्षाओं में उपयोगी क्यों है?`),
  ];

  const mistakeLine = bi(
    `Common mistake: students often mix the definition with the application. Separate the concept, example and final conclusion clearly.`,
    `आम गलती: विद्यार्थी अक्सर परिभाषा और अनुप्रयोग को मिला देते हैं। अवधारणा, उदाहरण और अंतिम निष्कर्ष को अलग-अलग स्पष्ट लिखें।`
  );

  const sections = [
    {
      id: 'overview',
      title: bi('Chapter Overview', 'अध्याय परिचय'),
      blocks: [
        { type: 'paragraph', text: introText },
        { type: 'note', text: hint.summary },
        { type: 'tip', text: bi('Read the chapter once for concepts, once for examples and once for revision. That three-pass method works well for school tests.', 'अध्याय को पहले अवधारणा के लिए, फिर उदाहरणों के लिए और अंत में revision के लिए पढ़ें। यह तीन-पास विधि school tests में बहुत मदद करती है।') },
      ],
    },
    {
      id: 'key-ideas',
      title: bi('Key Ideas', 'मुख्य बिंदु'),
      blocks: [
        { type: 'list', items: hint.focus },
        { type: 'warning', text: mistakeLine },
      ],
    },
    {
      id: 'worked-example',
      title: bi('Worked Example', 'हल किया हुआ उदाहरण'),
      blocks: [
        { type: 'example', number: 1, problem: hint.example.problem, solution: hint.example.solution, openSolution: true },
        { type: 'example', number: 2, problem: bi(`Apply the same idea in a second question on ${en}.`, `${hi} पर उसी विचार को दूसरे प्रश्न में लागू कीजिए।`), solution: answerLine, openSolution: false },
      ],
    },
    {
      id: 'quick-check',
      title: bi('Quick Check Questions', 'त्वरित अभ्यास प्रश्न'),
      blocks: [
        { type: 'list', ordered: true, items: questions },
        { type: 'note', text: bi('These are the kind of short answers a teacher can ask in class or on a worksheet.', 'ये वही छोटे उत्तर हैं जो शिक्षक class या worksheet में पूछ सकते हैं।') },
      ],
    },
    {
      id: 'mind-map',
      title: bi('Mind Map Snapshot', 'माइंड मैप झलक'),
      blocks: [
        { type: 'table', headers: [bi('Branch', 'शाखा'), bi('Idea', 'विचार')], rows: conceptMapRows },
        { type: 'tip', text: bi('Use this section to revise the whole chapter in 60 seconds before a test.', 'परीक्षा से पहले 60 सेकंड में पूरे अध्याय को revise करने के लिए इस section का उपयोग करें।') },
      ],
    },
    {
      id: 'school-test-bank',
      title: bi('School Test Bank', 'स्कूल टेस्ट बैंक'),
      blocks: [
        { type: 'list', ordered: true, items: testQuestions },
        { type: 'warning', text: bi('Answer first in keywords, then expand only if the question asks for explanation.', 'पहले keywords में उत्तर लिखें, फिर केवल explanation माँगे तो विस्तार करें।') },
      ],
    },
    {
      id: 'revision-sheet',
      title: bi('Quick Revision Sheet', 'त्वरित पुनरावृत्ति शीट'),
      blocks: [
        { type: 'table', headers: [bi('What to remember', 'क्या याद रखना है'), bi('Why it matters', 'यह क्यों महत्वपूर्ण है')], rows: [[hint.revision, bi('Use it for fast recall in tests and board revision.', 'परीक्षा और बोर्ड revision में तेज़ recall के लिए उपयोग करें।')]] },
        { type: 'tip', text: bi('Revise the chapter once from the example, once from the key ideas and once from the revision sheet before a test.', 'किसी test से पहले अध्याय को एक बार example से, एक बार key ideas से और एक बार revision sheet से पढ़ें।') },
      ],
    },
    {
      id: 'exam-focus',
      title: bi('Exam Focus', 'परीक्षा फोकस'),
      blocks: [
        { type: 'warning', text: bi(
          `Typical school questions from ${en} ask for definitions, short reasoning, diagram-based understanding and a quick application of the main formula or rule.`,
          `${hi} से school questions आमतौर पर परिभाषा, छोटा कारण, diagram-based समझ और मुख्य सूत्र या नियम के त्वरित अनुप्रयोग पर आधारित होते हैं।`
        ) },
      ],
    },
  ];

  if (subjectKey === 'science') {
    sections.splice(3, 0, {
      id: 'science-link',
      title: bi('NCERT Link', 'एनसीईआरटी लिंक'),
      blocks: [
        { type: 'paragraph', text: bi(`Connect ${en} with textbook language, daily-life examples and classroom activities.`, `${hi} को पाठ्यपुस्तक की भाषा, दैनिक जीवन के उदाहरणों और कक्षा गतिविधियों से जोड़ें।`) },
        { type: 'table', headers: [bi('Observe', 'अवलोकन'), bi('Learn', 'सीखें')], rows: [[bi('A practical or diagram-based question', 'एक प्रयोगात्मक या चित्र-आधारित प्रश्न'), bi('How the concept behaves in real life', 'अवधारणा वास्तविक जीवन में कैसे काम करती है')]] },
      ],
    });
  } else {
    sections.splice(3, 0, {
      id: 'math-link',
      title: bi('Formula Link', 'सूत्र लिंक'),
      blocks: [
        { type: 'paragraph', text: bi(`Always connect ${en} to the correct formula, property or step-by-step method.`, `हमेशा ${hi} को सही सूत्र, गुण या step-by-step विधि से जोड़ें।`) },
        { type: 'note', text: bi('If a question looks long, reduce it to the formula, the given values and the required answer.', 'यदि प्रश्न लंबा लगे तो उसे सूत्र, दिए गए मान और आवश्यक उत्तर में तोड़ दें।') },
      ],
    });
  }

  return sections;
}

function buildKeyFormulas({ subjectKey, title, chapterId }) {
  const english = resolveText(title, 'english');

  if (subjectKey === 'mathematics') {
    switch (english) {
      case 'Real Numbers':
        return [
          { latex: String.raw`a = \frac{p}{q},\ q \neq 0`, desc: bi('A rational number is written as a fraction of integers.', 'परिमेय संख्या को दो पूर्णांकों के भिन्न के रूप में लिखा जाता है।') },
          { latex: String.raw`\text{HCF} \times \text{LCM} = a \times b`, desc: bi('Useful for two numbers in standard school problems.', 'दो संख्याओं के मानक स्कूल प्रश्नों में उपयोगी संबंध।') },
        ];
      case 'Polynomials':
        return [
          { latex: String.raw`p(x) = ax^2 + bx + c`, desc: bi('Standard form of a quadratic polynomial.', 'द्विघात बहुपद का मानक रूप।') },
          { latex: String.raw`\alpha + \beta = -\frac{b}{a},\quad \alpha\beta = \frac{c}{a}`, desc: bi('Sum and product of zeroes for a quadratic polynomial.', 'द्विघात बहुपद के शून्यों का योग और गुणनफल।') },
        ];
      case 'Pair of Linear Equations in Two Variables':
        return [
          { latex: String.raw`a_1x + b_1y + c_1 = 0`, desc: bi('General form of a linear equation in two variables.', 'दो चर वाले रैखिक समीकरण का सामान्य रूप।') },
          { latex: String.raw`\frac{a_1}{a_2} = \frac{b_1}{b_2} \ne \frac{c_1}{c_2}`, desc: bi('Condition for parallel lines and no solution.', 'समांतर रेखाओं और कोई हल नहीं की स्थिति।') },
        ];
      case 'Quadratic Equations':
        return [
          { latex: String.raw`ax^2 + bx + c = 0`, desc: bi('Standard form of a quadratic equation.', 'द्विघात समीकरण का मानक रूप।') },
          { latex: String.raw`x = \frac{-b \pm \sqrt{b^2 - 4ac}}{2a}`, desc: bi('Quadratic formula used to find roots.', 'मूल ज्ञात करने का द्विघात सूत्र।') },
        ];
      case 'Arithmetic Progressions':
        return [
          { latex: String.raw`a_n = a + (n-1)d`, desc: bi('nth term of an arithmetic progression.', 'समान्तर श्रेणी का nवाँ पद।') },
          { latex: String.raw`S_n = \frac{n}{2}[2a + (n-1)d]`, desc: bi('Sum of first n terms of an arithmetic progression.', 'समान्तर श्रेणी के पहले n पदों का योग।') },
        ];
      case 'Triangles':
        return [
          { latex: String.raw`\frac{AB}{DE} = \frac{BC}{EF} = \frac{AC}{DF}`, desc: bi('Corresponding sides of similar triangles are proportional.', 'समरूप त्रिभुजों की संगत भुजाएँ समानुपाती होती हैं।') },
          { latex: String.raw`\frac{\text{Area of }\triangle_1}{\text{Area of }\triangle_2} = \left(\frac{\text{side}_1}{\text{side}_2}\right)^2`, desc: bi('Area ratio follows the square of side ratio.', 'क्षेत्रफल अनुपात भुजा अनुपात के वर्ग के बराबर होता है।') },
        ];
      case 'Coordinate Geometry':
        return [
          { latex: String.raw`P(x, y)`, desc: bi('Ordered pair used to locate a point.', 'बिंदु को व्यक्त करने के लिए क्रमित युग्म।') },
          { latex: String.raw`d = \sqrt{(x_2-x_1)^2 + (y_2-y_1)^2}`, desc: bi('Distance formula for two points.', 'दो बिंदुओं के बीच दूरी का सूत्र।') },
        ];
      case 'Introduction to Trigonometry':
        return [
          { latex: String.raw`\sin\theta = \frac{\text{opposite}}{\text{hypotenuse}}`, desc: bi('Basic trigonometric ratio.', 'मूल त्रिकोणमितीय अनुपात।') },
          { latex: String.raw`\sin^2\theta + \cos^2\theta = 1`, desc: bi('Fundamental trigonometric identity.', 'मूल त्रिकोणमितीय सर्वसमिका।') },
        ];
      case 'Some Applications of Trigonometry':
        return [
          { latex: String.raw`\tan\theta = \frac{\text{height}}{\text{distance}}`, desc: bi('Used in height and distance problems.', 'ऊँचाई और दूरी के प्रश्नों में उपयोगी।') },
          { latex: String.raw`\text{angle of elevation} = \text{angle of depression}`, desc: bi('Angles formed with horizontal lines are equal in corresponding situations.', 'क्षैतिज रेखा के साथ बने संबंधित कोण समान होते हैं।') },
        ];
      case 'Circles':
        return [
          { latex: String.raw`OP \perp PT`, desc: bi('Radius to the point of contact is perpendicular to tangent.', 'स्पर्श बिंदु पर त्रिज्या स्पर्शरेखा पर लम्ब होती है।') },
          { latex: String.raw`PA = PB`, desc: bi('Tangents from the same external point are equal.', 'एक ही बाह्य बिंदु से खींची स्पर्शरेखाएँ बराबर होती हैं।') },
        ];
      case 'Constructions':
        return [
          { latex: String.raw`\text{Given data} \rightarrow \text{Draw base} \rightarrow \text{Mark arcs} \rightarrow \text{Join points}`, desc: bi('Construction questions are solved step by step with compass and ruler.', 'निर्माण प्रश्न कम्पास और स्केल से चरणबद्ध हल किए जाते हैं।') },
        ];
      case 'Areas Related to Circles':
        return [
          { latex: String.raw`A = \frac{\theta}{360^\circ}\pi r^2`, desc: bi('Area of a sector of a circle.', 'वृत्त के सेक्टर का क्षेत्रफल।') },
          { latex: String.raw`C = 2\pi r`, desc: bi('Circumference of a circle.', 'वृत्त की परिधि।') },
        ];
      case 'Surface Areas and Volumes':
        return [
          { latex: String.raw`V = lbh`, desc: bi('Volume of a cuboid.', 'घनाभ का आयतन।') },
          { latex: String.raw`V = \pi r^2h`, desc: bi('Volume of a cylinder.', 'बेलन का आयतन।') },
          { latex: String.raw`V = \frac{4}{3}\pi r^3`, desc: bi('Volume of a sphere.', 'गोले का आयतन।') },
        ];
      case 'Statistics':
        return [
          { latex: String.raw`\bar{x} = \frac{\sum x_i}{n}`, desc: bi('Mean of a set of observations.', 'अवलोकनों का माध्य।') },
          { latex: String.raw`\text{Median} = \text{middle value}`, desc: bi('Median is the central value after arrangement.', 'क्रम में रखने के बाद मध्य मान माध्यिका होता है।') },
        ];
      case 'Probability':
        return [
          { latex: String.raw`P(E) = \frac{n(E)}{n(S)}`, desc: bi('Probability of an event.', 'किसी घटना की प्रायिकता।') },
        ];
      // ── Class 9 math ───────────────────────────────────────────────────
      case 'Number Systems':
        return [
          { latex: String.raw`\frac{p}{q},\;q\neq 0`, desc: bi('Rational number in lowest terms.', 'सरलतम रूप में परिमेय संख्या।') },
          { latex: String.raw`a^m \times a^n = a^{m+n}`, desc: bi('Law of exponents for real numbers.', 'वास्तविक संख्याओं के लिए घातांक नियम।') },
        ];
      case 'Linear Equations in Two Variables':
        return [
          { latex: String.raw`ax + by + c = 0`, desc: bi('General form of a linear equation in two variables.', 'दो चर वाले रैखिक समीकरण का सामान्य रूप।') },
        ];
      case "Introduction to Euclid's Geometry":
        return [
          { latex: String.raw`\text{Axiom 1: Things equal to the same thing are equal to one another.}`, desc: bi("One of Euclid's axioms.", 'यूक्लिड के एक अभिगृहीत।') },
        ];
      case 'Lines and Angles':
        return [
          { latex: String.raw`\angle A + \angle B + \angle C = 180°`, desc: bi('Angle sum of a triangle.', 'त्रिभुज के कोणों का योग।') },
          { latex: String.raw`\text{Exterior angle} = \text{sum of non-adjacent interior angles}`, desc: bi('Exterior angle theorem.', 'बाह्य कोण प्रमेय।') },
        ];
      case 'Quadrilaterals':
        return [
          { latex: String.raw`\angle A + \angle B + \angle C + \angle D = 360°`, desc: bi('Sum of interior angles of a quadrilateral.', 'चतुर्भुज के अंतः कोणों का योग।') },
        ];
      case 'Areas of Parallelograms and Triangles':
        return [
          { latex: String.raw`\text{Area of parallelogram} = b \times h`, desc: bi('Base times height.', 'आधार × ऊँचाई।') },
          { latex: String.raw`\text{Area of triangle} = \tfrac{1}{2} b \times h`, desc: bi('Half base times height.', '½ × आधार × ऊँचाई।') },
        ];
      case "Heron's Formula":
        return [
          { latex: String.raw`s = \frac{a+b+c}{2}`, desc: bi('Semi-perimeter.', 'अर्ध-परिमाप।') },
          { latex: String.raw`A = \sqrt{s(s-a)(s-b)(s-c)}`, desc: bi("Heron's formula for triangle area.", 'हेरॉन का क्षेत्रफल सूत्र।') },
        ];
      // ── Class 8 math ───────────────────────────────────────────────────
      case 'Rational Numbers':
        return [
          { latex: String.raw`\frac{p}{q},\;q\neq 0`, desc: bi('Standard form of a rational number.', 'परिमेय संख्या का मानक रूप।') },
          { latex: String.raw`\frac{p}{q} \div \frac{r}{s} = \frac{p}{q} \times \frac{s}{r}`, desc: bi('Division of rational numbers.', 'परिमेय संख्याओं का भाग।') },
        ];
      case 'Linear Equations in One Variable':
        return [
          { latex: String.raw`ax + b = c \;\Rightarrow\; x = \frac{c-b}{a}`, desc: bi('General solution of a one-variable equation.', 'एक चर समीकरण का सामान्य हल।') },
        ];
      case 'Understanding Quadrilaterals':
        return [
          { latex: String.raw`\sum \text{interior angles} = (n-2)\times 180°`, desc: bi('Angle sum for an n-sided polygon.', 'n भुजा वाले बहुभुज के कोणों का योग।') },
          { latex: String.raw`\sum \text{exterior angles} = 360°`, desc: bi('Sum of exterior angles of any polygon.', 'किसी बहुभुज के बाह्य कोणों का योग।') },
        ];
      case 'Squares and Square Roots':
        return [
          { latex: String.raw`\sqrt{a \times b} = \sqrt{a} \times \sqrt{b}`, desc: bi('Product rule for square roots.', 'वर्गमूल के लिए गुणन नियम।') },
        ];
      case 'Cubes and Cube Roots':
        return [
          { latex: String.raw`\sqrt[3]{a \times b} = \sqrt[3]{a} \times \sqrt[3]{b}`, desc: bi('Product rule for cube roots.', 'घनमूल के लिए गुणन नियम।') },
        ];
      case 'Comparing Quantities':
        return [
          { latex: String.raw`\text{Profit\%} = \frac{\text{SP}-\text{CP}}{\text{CP}} \times 100`, desc: bi('Percentage profit formula.', 'प्रतिशत लाभ सूत्र।') },
          { latex: String.raw`A = P\!\left(1+\frac{r}{100}\right)^{\!n}`, desc: bi('Compound interest formula.', 'चक्रवृद्धि ब्याज सूत्र।') },
        ];
      case 'Algebraic Expressions and Identities':
        return [
          { latex: String.raw`(a+b)^2 = a^2 + 2ab + b^2`, desc: bi('Square of a sum.', 'योग का वर्ग।') },
          { latex: String.raw`(a-b)^2 = a^2 - 2ab + b^2`, desc: bi('Square of a difference.', 'अंतर का वर्ग।') },
          { latex: String.raw`(a+b)(a-b) = a^2 - b^2`, desc: bi('Difference of squares identity.', 'वर्गों के अंतर की सर्वसमिका।') },
        ];
      case 'Mensuration':
        return [
          { latex: String.raw`A_{\text{trapezium}} = \tfrac{1}{2}(a+b)h`, desc: bi('Area of a trapezium.', 'समलंब चतुर्भुज का क्षेत्रफल।') },
          { latex: String.raw`\text{TSA of cuboid} = 2(lb+bh+hl)`, desc: bi('Total surface area of a cuboid.', 'घनाभ का पूर्ण पृष्ठीय क्षेत्रफल।') },
        ];
      case 'Exponents and Powers':
        return [
          { latex: String.raw`a^m \times a^n = a^{m+n}`, desc: bi('Product of same base.', 'समान आधार का गुणनफल।') },
          { latex: String.raw`a^0 = 1,\;a \neq 0`, desc: bi('Zero exponent rule.', 'शून्य घातांक नियम।') },
        ];
      case 'Direct and Inverse Proportions':
        return [
          { latex: String.raw`y = kx \;\Rightarrow\; \frac{y_1}{y_2} = \frac{x_1}{x_2}`, desc: bi('Direct proportion.', 'प्रत्यक्षानुपात।') },
          { latex: String.raw`xy = k \;\Rightarrow\; x_1 y_1 = x_2 y_2`, desc: bi('Inverse proportion.', 'व्युत्क्रमानुपात।') },
        ];
      case 'Factorisation':
        return [
          { latex: String.raw`a^2 - b^2 = (a+b)(a-b)`, desc: bi('Difference of squares.', 'वर्गों का अंतर।') },
        ];
      // ── Class 7 math ───────────────────────────────────────────────────
      case 'Integers':
        return [
          { latex: String.raw`(-a)\times(-b) = ab`, desc: bi('Product of two negatives is positive.', 'दो ऋणात्मकों का गुणनफल धनात्मक होता है।') },
        ];
      case 'Fractions and Decimals':
        return [
          { latex: String.raw`\frac{a}{b} \times \frac{c}{d} = \frac{ac}{bd}`, desc: bi('Multiplication of fractions.', 'भिन्नों का गुणन।') },
          { latex: String.raw`\frac{a}{b} \div \frac{c}{d} = \frac{a}{b} \times \frac{d}{c}`, desc: bi('Division of fractions.', 'भिन्नों का भाग।') },
        ];
      case 'Simple Equations':
        return [
          { latex: String.raw`ax + b = c \Rightarrow x = \frac{c-b}{a}`, desc: bi('Solving a simple equation.', 'सरल समीकरण हल करना।') },
        ];
      case 'The Triangle and its Properties':
        return [
          { latex: String.raw`\angle A + \angle B + \angle C = 180°`, desc: bi('Angle sum property.', 'कोण-योग गुणधर्म।') },
          { latex: String.raw`a^2 + b^2 = c^2`, desc: bi('Pythagoras theorem (right triangle).', 'पायथागोरस प्रमेय (समकोण त्रिभुज)।') },
        ];
      case 'Perimeter and Area':
        return [
          { latex: String.raw`A_{\triangle} = \tfrac{1}{2} \times b \times h`, desc: bi('Area of a triangle.', 'त्रिभुज का क्षेत्रफल।') },
          { latex: String.raw`A_{\text{circle}} = \pi r^2`, desc: bi('Area of a circle.', 'वृत्त का क्षेत्रफल।') },
        ];
      case 'Algebraic Expressions':
        return [
          { latex: String.raw`(a+b)^2 = a^2 + 2ab + b^2`, desc: bi('Useful identity for expansion.', 'विस्तार के लिए उपयोगी सर्वसमिका।') },
        ];
      // ── Class 6 math ───────────────────────────────────────────────────
      case 'Playing with Numbers':
        return [
          { latex: String.raw`\text{LCM} \times \text{HCF} = a \times b`, desc: bi('Relation between LCM and HCF.', 'LCM और HCF का संबंध।') },
        ];
      case 'Ratio and Proportion':
        return [
          { latex: String.raw`a : b = c : d \Rightarrow ad = bc`, desc: bi('Proportion: product of means = product of extremes.', 'समानुपात: मध्य पदों का गुणनफल = अंत्य पदों का गुणनफल।') },
        ];
      case 'Comparing Quantities':
        return [
          { latex: String.raw`\text{Percentage} = \frac{\text{part}}{\text{whole}} \times 100`, desc: bi('Basic percentage formula.', 'मूल प्रतिशत सूत्र।') },
        ];
      default:
        return [];
    }
  }

  switch (english) {
    case 'Food: Where Does It Come From?':
      return [
        { latex: String.raw`\text{Plants} \rightarrow \text{food sources}`, desc: bi('Food comes from plants as well as animals.', 'भोजन पौधों और जंतुओं दोनों से मिलता है।') },
      ];
    case 'Components of Food':
      return [
        { latex: String.raw`\text{Balanced diet} = \text{nutrients} + \text{roughage} + \text{water}`, desc: bi('A balanced diet must include all major components.', 'संतुलित आहार में सभी प्रमुख घटक होने चाहिए।') },
      ];
    case 'Fibre to Fabric':
      return [
        { latex: String.raw`\text{fibre} \rightarrow \text{yarn} \rightarrow \text{fabric}`, desc: bi('Clothes are made through spinning and weaving.', 'कपड़े कताई और बुनाई से बनते हैं।') },
      ];
    case 'Sorting Materials into Groups':
      return [
        { latex: String.raw`\text{density} = \frac{\text{mass}}{\text{volume}}`, desc: bi('Useful in comparing and grouping materials.', 'पदार्थों की तुलना और वर्गीकरण में उपयोगी।') },
      ];
    case 'Separation of Substances':
      return [
        { latex: String.raw`\text{mixture} \rightarrow \text{separation method}`, desc: bi('Choose the method based on the type of mixture.', 'मिश्रण के प्रकार के अनुसार विधि चुनें।') },
      ];
    case 'Changes Around Us':
      return [
        { latex: String.raw`\text{reversible change} \leftrightarrow \text{original state}`, desc: bi('Some changes can be undone.', 'कुछ परिवर्तन वापस उलटे जा सकते हैं।') },
      ];
    case 'Getting to Know Plants':
      return [
        { latex: String.raw`\text{root} : \text{absorption}`, desc: bi('Roots absorb water and minerals.', 'जड़ें जल और खनिज अवशोषित करती हैं।') },
      ];
    case 'Body Movements':
      return [
        { latex: String.raw`\text{joint} + \text{muscle} = \text{movement}`, desc: bi('Bones, joints and muscles work together.', 'हड्डियाँ, जोड़ और मांसपेशियाँ मिलकर गति कराते हैं।') },
      ];
    case 'The Living Organisms and Their Surroundings':
      return [
        { latex: String.raw`\text{adaptation} \rightarrow \text{survival}`, desc: bi('Adaptation helps an organism survive in its habitat.', 'अनुकूलन जीव को उसके आवास में जीवित रहने में मदद करता है।') },
      ];
    case 'Motion':
    case 'Motion and Measurement of Distances':
      return [
        { latex: String.raw`v = \frac{d}{t}`, desc: bi('Speed = distance divided by time.', 'चाल = दूरी / समय।') },
      ];
    case 'Force and Laws of Motion':
      return [
        { latex: String.raw`F = ma`, desc: bi('Newton’s second law of motion.', 'न्यूटन का दूसरा गति नियम।') },
        { latex: String.raw`p = mv`, desc: bi('Momentum is mass multiplied by velocity.', 'संवेग = द्रव्यमान × वेग।') },
      ];
    case 'Gravitation':
      return [
        { latex: String.raw`W = mg`, desc: bi('Weight equals mass multiplied by gravity.', 'भार = द्रव्यमान × गुरुत्वीय त्वरण।') },
        { latex: String.raw`F = G\frac{m_1m_2}{r^2}`, desc: bi('Universal law of gravitation.', 'सार्वत्रिक गुरुत्वाकर्षण का नियम।') },
      ];
    case 'Work and Energy':
      return [
        { latex: String.raw`W = F\times s`, desc: bi('Work done by a force.', 'बल द्वारा किया गया कार्य।') },
        { latex: String.raw`P = \frac{W}{t}`, desc: bi('Power is work done per unit time.', 'शक्ति = प्रति इकाई समय किया गया कार्य।') },
      ];
    case 'Sound':
      return [
        { latex: String.raw`v = \nu\lambda`, desc: bi('Wave speed relation.', 'तरंग वेग का संबंध।') },
      ];
    case 'Electricity':
    case 'Electricity and Circuits':
      return [
        { latex: 'V = IR', desc: bi('Ohm’s law.', 'ओम का नियम।') },
        { latex: 'P = VI', desc: bi('Electric power relation.', 'विद्युत शक्ति का संबंध।') },
      ];
    case 'Light - Reflection and Refraction':
      return [
        { latex: '\frac{1}{f} = \frac{1}{v} + \frac{1}{u}', desc: bi('Lens formula used in ray optics.', 'किरण प्रकाशिकी में लेंस सूत्र।') },
      ];
    case 'Chemical Reactions & Equations':
      return [
        { latex: '\text{Reactants} \rightarrow \text{Products}', desc: bi('A chemical equation shows what changes into what.', 'रासायनिक समीकरण यह दिखाता है कि क्या किसमें बदलता है।') },
        { latex: '\sum \text{atoms before} = \sum \text{atoms after}', desc: bi('Mass is conserved in a balanced equation.', 'संतुलित समीकरण में द्रव्यमान संरक्षित रहता है।') },
      ];
    case 'Acids, Bases and Salts':
      return [
        { latex: '\text{pH} < 7', desc: bi('Acidic nature.', 'अम्लीय प्रकृति।') },
        { latex: '\text{pH} = 7', desc: bi('Neutral nature.', 'तटस्थ प्रकृति।') },
        { latex: '\text{Acid} + \text{Base} \rightarrow \text{Salt} + \text{Water}', desc: bi('Neutralisation reaction.', 'उदासीनीकरण अभिक्रिया।') },
      ];
    case 'Metals and Non-Metals':
      return [
        { latex: '\text{Metal} \rightarrow \text{Metal}^{n+} + ne^-', desc: bi('Metals tend to lose electrons.', 'धातुएँ इलेक्ट्रॉन खोती हैं।') },
      ];
    case 'Carbon and its Compounds':
      return [
        { latex: '\text{C}_n\text{H}_{2n+2}', desc: bi('General formula of alkanes.', 'अल्केन का सामान्य सूत्र।') },
      ];
    case 'Life Processes':
      return [
        { latex: String.raw`\text{Glucose} + \text{O}_2 \rightarrow \text{CO}_2 + \text{H}_2\text{O} + \text{Energy}`, desc: bi('Aerobic respiration summary.', 'वायवीय श्वसन का सार।') },
      ];
    case 'Heredity and Evolution':
      return [
        { latex: String.raw`3:1`, desc: bi('Typical phenotypic ratio in a monohybrid cross.', 'एकल संकरण में सामान्य फीनोटाइप अनुपात।') },
      ];
    case 'Human Eye and Colourful World':
      return [
        { latex: String.raw`\lambda \uparrow \Rightarrow \text{scattering} \downarrow`, desc: bi('Longer wavelengths scatter less.', 'लंबी तरंगदैर्घ्य कम प्रकीर्णित होती है।') },
      ];
    case 'Magnetic Effects of Electric Current':
      return [
        { latex: String.raw`F = BIL\sin\theta`, desc: bi('Force on a current-carrying conductor in a magnetic field.', 'चुंबकीय क्षेत्र में धारा-वाहक चालक पर लगने वाला बल।') },
      ];
    // ── Class 7 science ────────────────────────────────────────────────────
    case 'Nutrition in Plants':
      return [
        { latex: String.raw`6\text{CO}_2 + 6\text{H}_2\text{O} \xrightarrow{\text{light}} \text{C}_6\text{H}_{12}\text{O}_6 + 6\text{O}_2`, desc: bi('Photosynthesis equation.', 'प्रकाश-संश्लेषण समीकरण।') },
      ];
    case 'Heat':
      return [
        { latex: String.raw`Q = mc\Delta T`, desc: bi('Heat gained or lost by a body.', 'किसी पिंड द्वारा ग्रहण या त्यागी गई ऊष्मा।') },
      ];
    case 'Respiration in Organisms':
      return [
        { latex: String.raw`\text{Glucose} + \text{O}_2 \rightarrow \text{CO}_2 + \text{H}_2\text{O} + \text{Energy}`, desc: bi('Aerobic respiration.', 'वायवीय श्वसन।') },
      ];
    case 'Motion and Time':
      return [
        { latex: String.raw`\text{Speed} = \frac{\text{Distance}}{\text{Time}}`, desc: bi('Basic speed formula.', 'मूल चाल सूत्र।') },
      ];
    // ── Class 8 science ────────────────────────────────────────────────────
    case 'Force and Pressure':
      return [
        { latex: String.raw`P = \frac{F}{A}`, desc: bi('Pressure = Force per unit area.', 'दाब = बल / क्षेत्रफल।') },
      ];
    case 'Combustion and Flame':
      return [
        { latex: String.raw`\text{Fuel} + \text{O}_2 \rightarrow \text{CO}_2 + \text{H}_2\text{O} + \text{Heat}`, desc: bi('General combustion reaction.', 'सामान्य दहन अभिक्रिया।') },
      ];
    case 'Cell - Structure and Functions':
      return [
        { latex: String.raw`\text{Cell membrane} \rightarrow \text{selective permeability}`, desc: bi('The cell membrane controls what enters and exits.', 'कोशिका झिल्ली नियंत्रित करती है कि क्या प्रवेश करता और क्या बाहर जाता है।') },
      ];
    default:
      return [];
  }
}

function buildChapter({ programKey, classKey, classLabel, subjectKey, subjectLabel, chapterDef, catalog, index }) {
  const title = chapterDef.title || bi(humanizeChapterId(chapterDef.id), humanizeChapterId(chapterDef.id));
  const hint = getHintForChapter({ programKey, subjectKey, title, chapterId: chapterDef.id, classLabel });
  const sections = buildSectionList({ subjectKey, title, hint, classLabel });
  const keyFormulas = buildKeyFormulas({ subjectKey, title, chapterId: chapterDef.id });

  return {
    id: chapterDef.id,
    slug: chapterDef.id,
    title,
    subject: subjectKey,
    subjectLabel,
    exam: `${classLabel} Board`,
    weightage: 'Board and class test friendly',
    syllabusTags: [classLabel.replace('Class ', 'Class-'), subjectLabel, 'CBSE', 'Foundation'],
    sections,
    keyFormulas,
  };
}

function buildProgram({ programKey, classKey, classLabel, subjectKey, subjectLabel, catalog }) {
  const chapterBlueprints = getProgramBlueprints(programKey, catalog);
  const chapters = chapterBlueprints.map((chapterDef, index) => buildChapter({ programKey, classKey, classLabel, subjectKey, subjectLabel, chapterDef, catalog, index }));

  return {
    key: `${classKey}-${subjectKey}`,
    classKey,
    classLabel,
    subjectLabel,
    title: bi(
      `${classLabel} ${subjectLabel} Notes`,
      `${classLabel} ${subjectKey === 'science' ? 'विज्ञान' : 'गणित'} नोट्स`,
    ),
    subtitle: bi(
      `Shared chapterwise notes for ${classLabel} ${subjectLabel} with school-board focus, quick revision and exam-ready examples.`,
      `${classLabel} ${subjectKey === 'science' ? 'विज्ञान' : 'गणित'} के chapterwise नोट्स — स्कूल बोर्ड फोकस, त्वरित revision और परीक्षा-तैयार उदाहरणों के साथ।`,
    ),
    chapters,
  };
}

export const SCHOOL_PROGRAMS = PROGRAM_ORDER.map(({ classKey, subjectKey }) => {
  const classLabel = CLASS_META.find((item) => item.key === classKey)?.label || classKey;
  const subject = SUBJECT_META.find((item) => item.key === subjectKey) || SUBJECT_META[0];
  const catalog = subjectKey === 'science' ? LOWER_SCIENCE_CHAPTERS : LOWER_MATH_CHAPTERS;
  return buildProgram({ programKey: `${classKey}-${subjectKey}`, classKey, classLabel, subjectKey, subjectLabel: subject.label, catalog });
});

export const SCHOOL_PROGRAM_MAP = SCHOOL_PROGRAMS.reduce((acc, program) => {
  acc[program.key] = program;
  return acc;
}, {});

export function getSchoolProgram(programKey) {
  return SCHOOL_PROGRAM_MAP[programKey];
}

export function getSchoolChapter(programKey, chapterSlug) {
  const program = SCHOOL_PROGRAM_MAP[programKey];
  if (!program) return null;
  return program.chapters.find((chapter) => chapter.slug === chapterSlug);
}

export function resolveSchoolText(content, language = 'english') {
  return resolveText(content, language);
}
